import React, { useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react';
const CatalogPage = lazy(() => import('./CatalogPage'));
const OrdersPage = lazy(() => import('./OrdersPage'));
import { normalizeImportedProduct } from './utils/smartLayer';
import { loadSettings as loadSmartLayerSettings, saveSettings as saveSmartLayerBridgeSettings, validateSettings as validateSmartLayerBridgeSettings, runSelfTest, runReadinessAudit, precheck as slPrecheck, generateProductIntelligence as slGenerate, buildCard as slBuildCard, buildDetail as slBuildDetail, checkDraftGate as slCheckDraftGate, buildMonitor as slBuildMonitor, interpretMonitor as slInterpretMonitor, buildExperiments as slBuildExperiments } from './lib/smartlayerBridge';
import { processSingleProduct, processBatch, reprocessProduct, swapTitle, repriceProduct, switchPlatform } from './lib/slPipeline';
import { warmUpWorker } from './utils/smartLayerWorker';
import { AmorliaDashboardProvider, useAmorliaDashboard } from './context/AmorliaDashboardContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import LoginScreen from './components/LoginScreen';
import MobileLayout from './components/MobileLayout';
import MobileModal from './components/MobileModal';
import Icon from './components/icons';
import HomePage from './pages/HomePage';
import AnalyticsPage from './pages/AnalyticsPage';

const RENDER_API = 'https://amorlia-scraper-production.up.railway.app';

/* ── shared styles ── */
const css = {
  btn: (bg = '#FF6B35', hoverBg = '#E85A25') => ({
    padding: '0.625rem 1.25rem', background: bg, color: '#fff', border: 'none',
    borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    transition: 'background 0.15s, transform 0.1s',
  }),
  input: { width: '100%', padding: '0.625rem 0.875rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.08)', color: '#f8fafc', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', transition: 'border-color 0.15s' },
  card: { background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' },
  label: { fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem', display: 'block' },
  colors: {
    primary: '#FF6B35', primaryHover: '#E85A25', primaryGlow: 'rgba(255,107,53,0.4)',
    secondary: '#00D4AA', secondaryGlow: 'rgba(0,212,170,0.3)',
    danger: '#FF4757', warning: '#FFA502', success: '#2ED573',
    bgBase: '#0A0A0F', bgElevated: '#111118',
    textPrimary: '#F8FAFC', textSecondary: '#94A3B8', textMuted: '#64748B',
  },
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  duration: 200,
};

// Image proxy: use weserv.nl as CORS-safe image CDN
const proxyUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.includes('weserv.nl') || url.includes('proxy-image')) return url;

  // Strip filters:format to prevent 403 Forbidden from Cloudflare
  const cleanUrl = url.replace(/filters:format\([a-z]+\)\//ig, '');

  // Dual-layer proxy: Try Weserv first for speed, fallback to our Render proxy with headers if blocked
  const renderProxy = `${RENDER_API}/proxy-image?url=${encodeURIComponent(cleanUrl)}`;
  return `https://images.weserv.nl/?url=${encodeURIComponent(cleanUrl)}&default=${encodeURIComponent(renderProxy)}`;
};

const getVariantImageSrc = (variant) => {
  const rawUrl = variant.image || (variant.images && variant.images[0]) || '';
  if (!rawUrl || rawUrl === '' || rawUrl === 'img-0') return null;
  if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) return rawUrl;
  return `${RENDER_API}/proxy-image?url=${encodeURIComponent(rawUrl)}`;
};

// Strip HTML from description for safe display in textareas
const stripHtmlForDisplay = (raw) => {
  if (!raw) return '';
  if (!/<[^>]+>/.test(raw)) return raw;
  return raw
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s{3,}/g, '\n')
    .trim();
};

/* ── Mobile detection hook ── */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function saveToCatalog(variant, slData = null) {
  const key = 'amorlia_catalog';
  let catalog = [];
  try { catalog = JSON.parse(localStorage.getItem(key) || '[]'); } catch {}
  const variantId = variant.id || ('cat_' + Date.now() + '_' + Math.random().toString(36).slice(2,6));
  if (catalog.find(e => e.id === variantId)) return false;

  // Clean description — strip HTML
  const cleanDesc = stripHtmlForDisplay(variant.description || '');

  // Clean item specifics — remove noise values
  const NOISE_VALUES_RE = /reseller|business benefit|clearance|free shipping|wholesale|dropship|factory|1-6free|NH\d{5,}/i;
  const cleanSpecs = Object.fromEntries(
    Object.entries(variant.itemSpecifics || {})
      .filter(([, v]) => v && !NOISE_VALUES_RE.test(String(v)))
      .map(([k, v]) => [k, String(v).trim()])
  );

  // Clean SKU
  const rawSku = variant.supplierSku || variant.sku || '';
  let cleanSku = rawSku;
  if (rawSku.includes(',') || rawSku.includes(':')) {
    const first = rawSku.split(/[,:\n]/)[0].trim();
    cleanSku = /^[A-Z0-9\-]{4,25}$/i.test(first) ? first : '';
  } else {
    cleanSku = rawSku.slice(0, 30);
  }

  const entry = {
    ...variant,
    id: variantId,
    description: cleanDesc,
    sku: cleanSku,
    supplierSku: cleanSku,
    itemSpecifics: cleanSpecs,
    brand: cleanSpecs.Brand || 'Unbranded',
    material: cleanSpecs.Material || '',
    category: variant.slDetection?.category || variant.category || 'Jewelry',
    catalogStatus: variant.status === 'published' ? 'published' : 'draft',
    stockLevel: typeof variant.stock === 'boolean' ? (variant.stock ? null : 0) : (variant.stock ?? null),
    tags: variant.tags || [],
    bundleInfo: null,
    addedToCatalogAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    publishedAt: variant.status === 'published' ? new Date().toISOString() : null,
    archivedAt: null,
    editHistory: [],
    linkedOrderIds: [],
    rawPayload: variant.rawData || null,
    generatedCard: slData?.card || null,
    detailPagePack: slData?.detail || null,
    finalDecision: slData?.card?.data?.finalDecision || null,
    sourceUrl: variant.sourceUrl || variant.rawData?.sourceUrl || '',
  };
  catalog.push(entry);
  try { localStorage.setItem(key, JSON.stringify(catalog)); } catch {}
  return true;
}

/* ── SwipeNavigator ── */
function SwipeNavigator({ children, currentIndex, onIndexChange }) {
  const trackRef = useRef(null);
  const startX = useRef(0);
  const deltaX = useRef(0);
  const isDragging = useRef(false);
  const count = React.Children.count(children);

  const setTrackPosition = (offset) => {
    if (trackRef.current) trackRef.current.style.transform = `translateX(calc(-${currentIndex * 100}% + ${offset}px))`;
  };

  const handleTouchStart = (e) => { startX.current = e.touches[0].clientX; deltaX.current = 0; isDragging.current = true; if (trackRef.current) trackRef.current.classList.add('dragging'); };
  const handleTouchMove = (e) => { if (!isDragging.current) return; deltaX.current = e.touches[0].clientX - startX.current; setTrackPosition(deltaX.current); };
  const handleTouchEnd = () => {
    isDragging.current = false;
    if (trackRef.current) trackRef.current.classList.remove('dragging');
    const threshold = 60;
    if (deltaX.current < -threshold && currentIndex < count - 1) onIndexChange(currentIndex + 1);
    else if (deltaX.current > threshold && currentIndex > 0) onIndexChange(currentIndex - 1);
    else setTrackPosition(0);
    deltaX.current = 0;
  };

  return (
    <div>
      <div className="swipe-progress"><div className="swipe-progress-fill" style={{ width: `${((currentIndex + 1) / count) * 100}%` }} /></div>
      <div style={{ textAlign: 'center', marginBottom: '8px' }}><span style={{ fontSize: '13px', color: '#64748B' }}>{currentIndex + 1} of {count}</span></div>
      <div className="swipe-container" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div ref={trackRef} className="swipe-track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
          {React.Children.map(children, child => (<div className="swipe-card">{child}</div>))}
        </div>
      </div>
    </div>
  );
}

/* ── MobileProductCard ── */
function MobileProductCard({ variant, onUpdate, onPublish, onRemove, onRewrite, onRescrapePrice, slData }) {
  const sl = slData || variant._sl || {};
  const slCard = sl.card || {};
  const slDetail = sl.detail || {};
  const slPrecheckData = sl.precheck || {};
  const badges = slCard.badges || [];
  const smartScore = slCard.smartScore;
  const opportunityScore = slCard.opportunityScore;
  const riskLevel = slCard.riskLevel;
  const riskTemperature = slCard.riskTemperature;
  const profitTemperature = slCard.profitTemperature;
  const finalDecision = slCard.finalDecision;
  const bestNextMove = slCard.bestNextMove;
  const complianceLocked = slCard.complianceLocked || slDetail.complianceLocked || false;
  const titleScore = slDetail.titleScore;
  const imageSafety = slDetail.imageSafety || {};
  const policyLocks = slDetail.policyLocks || [];
  const enrichedDescription = slDetail.enrichedDescription;
  const finalCopyPack = slDetail.finalCopyPack;
  const [publishing, setPublishing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const images = variant.images && variant.images.length > 0 ? variant.images : variant.image ? [variant.image] : [];
  const priceConfirmed = variant.priceConfirmed !== false && variant.price > 0;
  const pricing = (() => { try { return JSON.parse(localStorage.getItem('amorlia_pricing') || '{}'); } catch { return {}; } })();
  const ebayFeePct = parseFloat(pricing.ebayFeePct) || 13.25;
  const shippingCost = parseFloat(pricing.shippingCost) || 4.00;
  const processingPct = parseFloat(pricing.processingPct) || 3.0;
  const minMarginPct = parseFloat(pricing.minMarginPct) || 15;
  const minProfitTarget = parseFloat(pricing.targetProfit) || 5.00;
  const sourcePrice = variant.rawNihaoPrice || 0;
  const sellPrice = variant.price || 0;
  const canCalculate = sourcePrice > 0 && sellPrice > 0;
  const totalCost = canCalculate ? sourcePrice + (sellPrice * ebayFeePct / 100) + 0.30 + (sellPrice * processingPct / 100) + shippingCost : 0;
  const netProfit = canCalculate ? sellPrice - totalCost : null;
  const margin = canCalculate && sellPrice > 0 ? (netProfit / sellPrice) * 100 : 0;
  const marginOk = canCalculate && netProfit >= minProfitTarget;
  const marginColor = !canCalculate ? '#64748B' : netProfit >= minProfitTarget ? '#2ED573' : netProfit >= 3 ? '#FFA502' : '#FF4757';

  const handlePublish = async () => { setPublishing(true); await onPublish(variant); setPublishing(false); };
  const copyToClipboard = (text) => { if (!text) return; navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => { const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); setCopied(true); setTimeout(() => setCopied(false), 2000); }); };  const swapToAlt = () => { if (variant.altTitle) { onUpdate(variant.id, 'title', variant.altTitle); onUpdate(variant.id, 'altTitle', variant.title); } };

  return (
    <div className="glass-card gradient-border" style={{ padding: 0, overflow: 'hidden', animation: 'fadeSlideUp 0.3s ease both' }}>
      <div className="mobile-product-image" onClick={() => images[0] && setPreviewImg(images[0])}>
        {images[0] ? (
          <img src={proxyUrl(images[0])} alt={variant.variantName} onError={e => { e.target.onerror = null; e.target.src = images[0]; }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#475569', fontSize: '48px' }}>?</div>
        )}
        <div className="price-overlay"><span className="price-pill">${variant.price}</span></div>
        <div className="status-overlay">
          <span className={`status-badge ${marginOk && priceConfirmed ? 'status-badge--success' : 'status-badge--danger'}`}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: marginOk && priceConfirmed ? '#2ED573' : '#FF4757', display: 'inline-block' }} />
            {marginOk && priceConfirmed ? 'Ready' : 'Needs Price'}
          </span>
        </div>
        <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 4 }}>
          <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {expanded ? Icon.x(16) : Icon.edit(16)}
          </button>
          {onRemove && (
            <button onClick={e => { e.stopPropagation(); onRemove(variant.id); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,71,87,0.3)', border: 'none', color: '#FF4757', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {Icon.x(14)}
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '16px 20px' }}>
        {/* SmartLayer badges */}
        {badges.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
            {badges.map((b, i) => (
              <span key={i} style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: 9999, background: b.color || '#1e3a5f', color: b.textColor || '#93c5fd', fontWeight: 600 }}>{b.label || b}</span>
            ))}
          </div>
        )}
        <p style={{ color: '#64748B', fontSize: '13px', marginBottom: 4 }}>{variant.variantName || 'Product Variant'}</p>
        {variant.title && <p style={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.3, marginBottom: 8 }}>{variant.title}</p>}

        {/* SmartLayer quick chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {variant.slStatus && <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6, background: variant.slStatus === 'READY_TO_REVIEW' ? '#14532d' : variant.slStatus === 'NEEDS_OPTIMIZATION' ? '#78350f' : '#7f1d1d', color: variant.slStatus === 'READY_TO_REVIEW' ? '#86efac' : variant.slStatus === 'NEEDS_OPTIMIZATION' ? '#fbbf24' : '#fca5a5', fontWeight: 600 }}>{variant.slStatus === 'READY_TO_REVIEW' ? 'Cassini Optimized' : variant.slStatus === 'NEEDS_OPTIMIZATION' ? 'Needs Work' : 'Blocked'}</span>}
          {smartScore != null && <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6, background: '#1e3a5f', color: '#93c5fd', fontWeight: 600 }}>Smart: {smartScore}</span>}
          {variant.slConversion?.score?.score != null && <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6, background: '#1e3a5f', color: variant.slConversion.score.score >= 80 ? '#86efac' : variant.slConversion.score.score >= 60 ? '#fbbf24' : '#fca5a5', fontWeight: 600 }}>Score: {variant.slConversion.score.score}/100</span>}
          {riskTemperature && <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6, background: riskTemperature === 'hot' ? '#7f1d1d' : riskTemperature === 'warm' ? '#78350f' : '#14532d', color: riskTemperature === 'hot' ? '#fca5a5' : riskTemperature === 'warm' ? '#fbbf24' : '#86efac', fontWeight: 600 }}>Risk: {riskTemperature}</span>}
          {profitTemperature && <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6, background: profitTemperature === 'hot' ? '#7f1d1d' : profitTemperature === 'warm' ? '#78350f' : '#14532d', color: profitTemperature === 'hot' ? '#fca5a5' : profitTemperature === 'warm' ? '#fbbf24' : '#86efac', fontWeight: 600 }}>Profit: {profitTemperature}</span>}
          {variant.titleVariants && variant.titleVariants.length > 1 && <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6, background: '#1e3a5f', color: '#93c5fd', fontWeight: 600 }}>{variant.titleVariants.length} variants</span>}
        </div>

        {/* SmartLayer precheck route */}
        {slPrecheckData.route && (
          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 6, padding: '4px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
            Route: <strong style={{ color: slPrecheckData.route === 'pass' ? '#2ED573' : slPrecheckData.route === 'review' ? '#FFA502' : '#FF4757' }}>{slPrecheckData.route}</strong>
            {slPrecheckData.score != null && <span> | Score: {slPrecheckData.score}</span>}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: '13px', color: !sourcePrice || sourcePrice <= 0 ? '#ef4444' : '#64748B' }}>
            {!sourcePrice || sourcePrice <= 0 ? 'Source: Not scraped' : `Source: $${sourcePrice.toFixed(2)}`}
          </span>
          {canCalculate && (<><span style={{ color: '#334155' }}>|</span>
          <span style={{ fontSize: '13px', color: marginColor, fontWeight: 700 }}>Margin: {margin.toFixed(1)}%</span></>)}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => copyToClipboard(variant.title)} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: copied ? '#2ED573' : '#94A3B8', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            {copied ? Icon.check(12) : Icon.copy(12)} {copied ? 'Copied' : 'Copy'}
          </button>
          <button onClick={() => { const nextIdx = ((variant.activeTitleIndex || 0) + 1) % (variant.titleVariants?.length || 1); const updated = swapTitle(variant, nextIdx); onUpdate(variant.id, 'title', updated.title); onUpdate(variant.id, 'activeTitleIndex', updated.activeTitleIndex); }} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#94A3B8', fontSize: '13px', cursor: 'pointer' }}>Swap {variant.titleVariants?.length > 0 ? `${(variant.activeTitleIndex || 0) + 1}/${variant.titleVariants.length}` : 'Alt'}</button>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16, animation: 'fadeSlideUp 0.2s ease both' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={css.label}>eBay Title</label>
            <input value={variant.title} onChange={e => onUpdate(variant.id, 'title', e.target.value)} style={css.input} maxLength={80} />
            <TitleCounter length={variant.title.length} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={css.label}>Description</label>
              <button onClick={() => copyToClipboard(variant.description)} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: 4 }}>{Icon.copy(12)} Copy</button>
            </div>
            <textarea value={stripHtmlForDisplay(variant.description)} onChange={e => onUpdate(variant.id, 'description', e.target.value)} rows={4} style={{ ...css.input, resize: 'vertical', minHeight: '80px' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              {['factual', 'gifting', 'stackable'].map(voice => (
                <button key={voice} onClick={() => onRewrite && onRewrite(variant, voice)} style={{ flex: 1, padding: '8px 0', background: variant.voice === voice ? 'rgba(255,107,53,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${variant.voice === voice ? '#FF6B35' : 'rgba(255,255,255,0.08)'}`, borderRadius: 8, color: variant.voice === voice ? '#FF6B35' : '#94A3B8', fontSize: '13px', cursor: 'pointer', fontWeight: variant.voice === voice ? 700 : 400, textTransform: 'capitalize' }}>{voice}</button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={css.label}>Price ($)</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => onUpdate(variant.id, 'price', Math.max(0.99, (variant.price || 0) - 1))} style={{ padding: '8px 14px', background: 'rgba(255,71,87,0.15)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 8, cursor: 'pointer', fontSize: '15px', fontWeight: 700 }}>-$1</button>
              <input type="number" step="0.01" value={variant.price || ''} onChange={e => onUpdate(variant.id, 'price', parseFloat(e.target.value) || 0)} style={{ ...css.input, flex: 1, textAlign: 'center' }} />
              <button onClick={() => onUpdate(variant.id, 'price', +(variant.price || 0) + 1)} style={{ padding: '8px 14px', background: 'rgba(46,213,115,0.15)', color: '#2ED573', border: '1px solid rgba(46,213,115,0.2)', borderRadius: 8, cursor: 'pointer', fontSize: '15px', fontWeight: 700 }}>+$1</button>
            </div>
          </div>
          <button onClick={handlePublish} disabled={publishing || !priceConfirmed || !marginOk || complianceLocked} className="btn-primary-gradient" style={{ marginTop: 8 }}>
            {Icon.send(18)} {publishing ? 'Publishing...' : complianceLocked ? 'COMPLIANCE LOCKED' : priceConfirmed && marginOk ? 'PUBLISH TO EBAY' : 'ADJUST PRICE TO PUBLISH'}
          </button>

          {/* SmartLayer final decision / next move */}
          {finalDecision && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, fontSize: '0.75rem' }}>
              <div style={{ color: finalDecision.action === 'publish' ? '#2ED573' : finalDecision.action === 'review' ? '#FFA502' : '#FF4757', fontWeight: 700 }}>{finalDecision.label || finalDecision.action}</div>
              {bestNextMove && <div style={{ color: '#94a3b8', marginTop: 2 }}>{bestNextMove}</div>}
            </div>
          )}

          {/* SmartLayer policy locks */}
          {policyLocks.length > 0 && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(255,71,87,0.08)', borderRadius: 8, border: '1px solid rgba(255,71,87,0.15)' }}>
              <p style={{ color: '#FF4757', fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>Policy Locks</p>
              {policyLocks.map((lock, i) => <p key={i} style={{ color: '#fca5a5', fontSize: '0.7rem', margin: 0 }}>{lock.message || lock.id || lock}</p>)}
            </div>
          )}

          {/* SmartLayer copy actions */}
          {finalCopyPack && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {finalCopyPack.title && <button onClick={() => copyToClipboard(finalCopyPack.title)} style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#93c5fd', fontSize: '0.7rem', cursor: 'pointer' }}>Copy Final Title</button>}
              {finalCopyPack.description && <button onClick={() => copyToClipboard(finalCopyPack.description)} style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#93c5fd', fontSize: '0.7rem', cursor: 'pointer' }}>Copy Final Desc</button>}
              {enrichedDescription && <button onClick={() => copyToClipboard(enrichedDescription)} style={{ padding: '4px 10px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 6, color: '#c4b5fd', fontSize: '0.7rem', cursor: 'pointer' }}>Copy Enriched</button>}
            </div>
          )}

          {/* Negative margin warning from SmartLayer */}
          {margin < 0 && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#7f1d1d', borderRadius: 8, fontSize: '0.75rem', color: '#fca5a5' }}>
              Blocked: Negative margin ({margin.toFixed(1)}%). This product cannot be published until price or costs change.
            </div>
          )}

          <button onClick={() => { const ok = saveToCatalog(variant, sl); if(ok) { if(onRemove) onRemove(variant.id); } else { alert('Already in Catalog'); } }} style={{ marginTop: 6, padding: '10px', background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, cursor: 'pointer', fontSize: '14px', fontWeight: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {Icon.archive(16)} Save to Catalog
          </button>
          {!priceConfirmed && <p style={{ color: '#FFA502', fontSize: '13px', marginTop: 8, textAlign: 'center' }}>Set a price before publishing</p>}
          {/* Source price input when not scraped */}
          {(!sourcePrice || sourcePrice <= 0) && (
            <div style={{ marginTop: 8, padding: 10, background: '#1a0f0a', border: '1px solid #d4af37', borderRadius: 8 }}>
              <label style={{ fontSize: '12px', color: '#d4af37', fontWeight: 600, display: 'block', marginBottom: 6 }}>Enter source price to calculate margin:</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="number" min="0" step="0.01" placeholder="0.00" style={{ flex: 1, padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#f8fafc', fontSize: 13 }}
                  onChange={(e) => {
                    const newPrice = parseFloat(e.target.value);
                    if (!isNaN(newPrice) && newPrice > 0) {
                      onUpdate(variant.id, 'rawNihaoPrice', newPrice);
                      onUpdate(variant.id, 'sourcePrice', newPrice);
                      onUpdate(variant.id, 'priceConfirmed', true);
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {previewImg && (
        <MobileModal open={true} onClose={() => setPreviewImg(null)} title="Image Preview">
          <img src={proxyUrl(previewImg)} alt="" style={{ width: '100%', borderRadius: 8 }} onError={e => { e.target.onerror = null; e.target.src = previewImg; }} />
        </MobileModal>
      )}
    </div>
  );
}

/* ── Title Character Counter ── */
function TitleCounter({ length }) {
  let color, label;
  if (length <= 59) { color = '#ef4444'; label = 'Too short — Cassini underscore penalty risk'; }
  else if (length <= 74) { color = '#f59e0b'; label = 'Acceptable — optimize further'; }
  else if (length <= 80) { color = '#22c55e'; label = 'Cassini optimized'; }
  else { color = '#ef4444'; label = 'eBay will truncate. Shorten immediately.'; }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.375rem', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: '0.7rem', color }}>{label}</span>
      </div>
      <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color, fontWeight: 600 }}>{length}/80</span>
    </div>
  );
}

/* ── Keyword Chip Row ── */
function KeywordChips({ title, onAppend }) {
  const remaining = 80 - title.length;
  const allChips = ['Initial Necklace', 'Personalized', 'Dainty', 'Minimalist', 'Layered', 'Gift for Her', 'Gold Tone', '18 Inch Chain', 'Hypoallergenic', 'PVD Coated', 'Waterproof', 'Tarnish Free', 'Everyday Wear', 'Women Girls', 'Stainless', 'Adjustable 16-20in'];
  const titleWords = new Set(title.toLowerCase().split(/\s+/));
  const available = allChips.filter(chip => { const chipWords = chip.toLowerCase().split(/\s+/); return !chipWords.some(w => titleWords.has(w)); }).slice(0, 8);
  return (
    <div style={{ marginTop: '0.375rem' }}>
      <p style={{ fontSize: '0.65rem', color: '#475569', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick append</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
        {available.map(chip => {
          const cost = chip.length + 1;
          const fits = cost <= remaining;
          return (
            <button key={chip} onClick={() => fits && onAppend(chip)} disabled={!fits} style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', borderRadius: '9999px', border: `1px solid ${fits ? '#475569' : '#1e293b'}`, background: '#0f172a', color: fits ? '#94a3b8' : '#334155', cursor: fits ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '0.25rem', transition: 'all 0.15s' }}>
              {chip}<span style={{ fontSize: '0.6rem', color: fits ? '#475569' : '#1e293b' }}>+{cost}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Competitive Title Preview ── */
function TitlePreview({ variant }) {
  const [open, setOpen] = useState(false);
  const metal = variant.itemSpecifics?.Metal || 'Metal';
  const style = variant.itemSpecifics?.['Pendant Style'] || 'Style';
  const finish = variant.itemSpecifics?.Color || 'Finish';
  const stone = variant.itemSpecifics?.['Main Stone'] || 'Stone';
  const occasion = variant.itemSpecifics?.Occasion || 'Everyday';
  const patterns = [
    { label: 'A', title: `${metal} ${style} Pendant Necklace 18in Adjustable Women` },
    { label: 'B', title: `${finish} ${style} Charm Necklace ${occasion} Gift Women` },
    { label: 'C', title: `${style} ${metal} Necklace ${stone !== 'None' ? stone + ' ' : ''}Tarnish Free Daily Women` },
  ];
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.25rem', padding: 0 }}>
        {Icon.eye(12)} How top sellers title this {open ? '(close)' : ''}
      </button>
      {open && (
        <div style={{ marginTop: '0.375rem', background: '#0f172a', borderRadius: '0.375rem', padding: '0.625rem', border: '1px solid #1e293b' }}>
          {patterns.map(p => (
            <div key={p.label} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.375rem', alignItems: 'baseline' }}>
              <span style={{ fontSize: '0.65rem', color: '#475569', fontFamily: 'monospace', width: '12px' }}>{p.label}</span>
              <span style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>{p.title.substring(0, 80)}</span>
              <span style={{ fontSize: '0.6rem', color: p.title.length >= 75 && p.title.length <= 80 ? '#22c55e' : '#64748b' }}>{p.title.length}ch</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Margin Calculator Panel ── */
function MarginPanel({ variant }) {
  const [open, setOpen] = useState(false);
  const sourcePrice = variant.rawNihaoPrice || variant.sourcePrice || 0;
  const sellingPrice = variant.price || 0;
  const pricing = (() => { try { return JSON.parse(localStorage.getItem('amorlia_pricing') || '{}'); } catch { return {}; } })();
  const ebayFeePct = parseFloat(pricing.ebayFeePct) || 13.25;
  const shippingCost = parseFloat(pricing.shippingCost) || 4.00;
  const processingPct = parseFloat(pricing.processingPct) || 3.0;
  const minMarginPct = parseFloat(pricing.minMarginPct) || 40;

  const canCalculate = sourcePrice > 0 && sellingPrice > 0;
  const ebayFee = canCalculate ? +(sellingPrice * (ebayFeePct / 100)).toFixed(2) : 0;
  const listingFee = 0.35;
  const processingFee = canCalculate ? +(sellingPrice * (processingPct / 100)).toFixed(2) : 0;
  const totalCost = canCalculate ? +(sourcePrice + ebayFee + listingFee + processingFee + shippingCost).toFixed(2) : 0;
  const netProfit = canCalculate ? +(sellingPrice - totalCost).toFixed(2) : null;
  const margin = canCalculate && sellingPrice > 0 ? +((netProfit / sellingPrice) * 100).toFixed(1) : null;

  let statusColor, statusBg, statusText;
  if (!canCalculate) { statusColor = '#64748b'; statusBg = '#1e293b'; statusText = 'Enter source price to calculate margin.'; }
  else if (margin >= minMarginPct) { statusColor = '#22c55e'; statusBg = '#052e16'; statusText = `Above minimum (${minMarginPct}%)`; }
  else if (margin >= 25) { statusColor = '#f59e0b'; statusBg = '#422006'; statusText = 'Low margin. Consider raising price.'; }
  else { statusColor = '#ef4444'; statusBg = '#450a0a'; statusText = 'This listing loses money. Adjust before publishing.'; }
  const row = (label, value, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.8rem', ...(bold ? { fontWeight: 700, borderTop: '1px dashed #334155', marginTop: '0.25rem', paddingTop: '0.5rem' } : {}) }}>
      <span style={{ color: '#94a3b8' }}>{label}</span>
      <span style={{ color: bold ? '#f8fafc' : '#cbd5e1', fontFamily: 'monospace' }}>${value != null ? value.toFixed(2) : '0.00'}</span>
    </div>
  );
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer', fontSize: '0.7rem', padding: '0.25rem 0.625rem', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {Icon.eye(12)} {open ? 'Hide Margin' : 'View Margin'}
      </button>
      {open && (
        <div style={{ marginTop: '0.5rem', background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
          {row('SOURCE PRICE (scraped)', sourcePrice)}
          {row(`eBay Final Value Fee (${ebayFeePct}%)`, ebayFee)}
          {row('eBay Listing Fee', listingFee)}
          {row(`Payment Processing est. (${processingPct}%)`, processingFee)}
          {row('Estimated Shipping Cost', shippingCost)}
          {row('Total Cost to You', totalCost, true)}
          {row('Your Selling Price', sellingPrice, true)}
          {row('Net Profit Per Sale', netProfit, true)}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.8rem', borderTop: '1px dashed #334155', marginTop: '0.25rem', paddingTop: '0.5rem', fontWeight: 700 }}>
            <span style={{ color: '#94a3b8' }}>Profit Margin</span>
            <span style={{ color: statusColor, fontFamily: 'monospace' }}>{margin != null ? margin : 'N/A'}%</span>
          </div>
          <div style={{ marginTop: '0.5rem', background: statusBg, borderRadius: '0.375rem', padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
            <span style={{ fontSize: '0.75rem', color: statusColor, fontWeight: 600 }}>MARGIN STATUS: {statusText}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SEO Tag Generator ── */
function generateSEO(itemSpecifics) {
  const s = itemSpecifics || {};
  const metal = s.Metal || s['Base Metal'] || 'Stainless Steel';
  const stone = s['Main Stone'] || '';
  const style = s.Style || 'Pendant';
  const type = s.Type || 'Necklace';
  const color = s.Color || 'Gold';
  const theme = s.Theme || '';
  const occasion = s.Occasion || 'Casual';
  const pendantStyle = s['Pendant Style'] || '';
  const lower = (str) => (str || '').toLowerCase();
  
  const typeLower = lower(type) || 'necklace';
  const colorAdj = lower(color).replace('tone', '').trim() || 'gold';
  const metalAdj = lower(metal).replace('-tone', '').replace('tone', '').trim();
  
  const variedPool = ['minimalist', 'handmade', 'gift for her', 'dainty', 'trendy', 'casual', 'everyday', 'boutique', 'fashion', 'boho', 'elegant', 'modern', 'vintage', 'statement'];
  
  // eBay
  let ebayTags = new Set();
  if (pendantStyle) ebayTags.add(`${lower(pendantStyle)} ${typeLower}`);
  ebayTags.add(`${colorAdj} ${typeLower}`);
  ebayTags.add(`${metalAdj} ${typeLower}`);
  if (stone && stone !== 'No Stone') ebayTags.add(`${lower(stone)} ${typeLower}`);
  ebayTags.add(`everyday ${typeLower} women`);
  ebayTags.add('gift for her');
  ebayTags.add(`tarnish resistant ${typeLower}`);
  
  const shuffledEbay = [...variedPool].sort(() => 0.5 - Math.random());
  for (let i = 0; ebayTags.size < 12 && i < shuffledEbay.length; i++) {
    ebayTags.add(`${shuffledEbay[i]} ${typeLower}`);
  }
  const ebayKeywords = Array.from(ebayTags).slice(0, 12).map(t => t.trim()).filter(Boolean);

  // Etsy
  let etsyTagsSet = new Set();
  etsyTagsSet.add(`${colorAdj} ${typeLower}`.substring(0, 20));
  etsyTagsSet.add(`${metalAdj} ${typeLower}`.substring(0, 20));
  if (pendantStyle) etsyTagsSet.add(`${lower(pendantStyle)} ${typeLower}`.substring(0, 20));
  etsyTagsSet.add('minimalist jewelry');
  etsyTagsSet.add(`dainty ${typeLower}`.substring(0, 20));
  etsyTagsSet.add('gift for her');
  
  const shuffledEtsy = [...variedPool].sort(() => 0.5 - Math.random());
  for (let i = 0; etsyTagsSet.size < 13 && i < shuffledEtsy.length; i++) {
    etsyTagsSet.add(`${shuffledEtsy[i]} ${typeLower}`.substring(0, 20));
  }
  
  // Fill any remaining with pure varied terms
  for (let i = 0; etsyTagsSet.size < 13 && i < variedPool.length; i++) {
    etsyTagsSet.add(variedPool[i]);
  }
  const etsyTags = Array.from(etsyTagsSet).slice(0, 13);
  
  const isLetter = lower(pendantStyle).includes('letter') || lower(theme).includes('letter');
  const isGold = lower(color).includes('gold');
  const isSilver = lower(color).includes('silver');
  const isRose = lower(color).includes('rose');
  const pinTitle = [pendantStyle ? `${pendantStyle}` : '', typeLower, isGold ? 'Gold' : isSilver ? 'Silver' : isRose ? 'Rose Gold' : colorAdj, `${metalAdj}`, isLetter ? 'Personalized' : 'Everyday Wear'].filter(Boolean).join(' | ').substring(0, 100);
  const pinDesc = `A ${isLetter ? 'clean round disc pendant' : pendantStyle ? `${lower(pendantStyle)} pendant` : `${colorAdj} pendant`} in ${colorAdj}-toned ${metalAdj}. ${isLetter ? 'Initial necklace for women' : `${style} ${typeLower}`} for everyday wear. Adjustable 18-inch chain, tarnish-resistant finish. Shop ${isLetter ? 'personalized' : 'fine'} jewelry at Amorlia Jewels. #${(colorAdj + 'Necklace').replace(/\s/g, '')} #${isLetter ? 'InitialPendant' : pendantStyle ? pendantStyle + typeLower.charAt(0).toUpperCase() + typeLower.slice(1) : 'MinimalistJewelry'} #MinimalistJewelry #GiftForHer`.substring(0, 500);
  
  return { ebayKeywords, etsyTags, pinTitle, pinDesc };
}

/* ── SEO Panel ── */
function SEOPanel({ itemSpecifics }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('ebay');
  const [copied, setCopied] = useState(null);
  const seo = generateSEO(itemSpecifics);
  const safeCopy = (text) => { try { navigator.clipboard.writeText(text).catch(() => { const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }); } catch { const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); } };
  const copySingle = (text, idx) => { safeCopy(text); setCopied(`${tab}-${idx}`); setTimeout(() => setCopied(null), 1500); };
  const copyAll = () => { const text = tab === 'ebay' ? seo.ebayKeywords.join(' | ') : tab === 'etsy' ? seo.etsyTags.join('\n') : `${seo.pinTitle}\n\n${seo.pinDesc}`; safeCopy(text); setCopied('all'); setTimeout(() => setCopied(null), 1500); };
  const tabs = [{ key: 'ebay', label: 'eBay Keywords' }, { key: 'etsy', label: 'Etsy Tags' }, { key: 'pinterest', label: 'Pinterest' }];
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <button onClick={() => setOpen(!open)} style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer', fontSize: '0.7rem', padding: '0.25rem 0.625rem', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {Icon.link(12)} {open ? 'Hide SEO Tags' : 'SEO Tags'}
      </button>
      {open && (
        <div style={{ marginTop: '0.5rem', background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.75rem' }}>
            {tabs.map(t => (<button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, padding: '0.375rem 0', background: tab === t.key ? '#1e3a5f' : 'transparent', border: `1px solid ${tab === t.key ? '#3b82f6' : '#334155'}`, borderRadius: '0.375rem', color: tab === t.key ? '#93c5fd' : '#64748b', fontSize: '0.7rem', cursor: 'pointer', fontWeight: tab === t.key ? 700 : 400 }}>{t.label}</button>))}
            <button onClick={copyAll} style={{ padding: '0.375rem 0.5rem', background: '#14532d', border: '1px solid #22c55e', borderRadius: '0.375rem', color: '#86efac', fontSize: '0.6rem', cursor: 'pointer', fontWeight: 600 }}>{copied === 'all' ? 'Copied!' : 'Copy All'}</button>
          </div>
          {tab === 'ebay' && (<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>{seo.ebayKeywords.map((kw, i) => (<button key={i} onClick={() => copySingle(kw, i)} style={{ background: copied === `ebay-${i}` ? '#14532d' : '#1e293b', border: '1px solid #334155', borderRadius: '1rem', padding: '0.25rem 0.625rem', fontSize: '0.7rem', color: copied === `ebay-${i}` ? '#86efac' : '#cbd5e1', cursor: 'pointer' }}>{kw}</button>))}</div>)}
          {tab === 'etsy' && (<div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>{seo.etsyTags.map((tag, i) => (<button key={i} onClick={() => copySingle(tag, i)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: copied === `etsy-${i}` ? '#14532d' : '#1e293b', border: '1px solid #334155', borderRadius: '0.375rem', padding: '0.3rem 0.625rem', fontSize: '0.7rem', color: copied === `etsy-${i}` ? '#86efac' : '#cbd5e1', cursor: 'pointer', textAlign: 'left' }}><span>{i + 1}. {tag}</span><span style={{ color: tag.length >= 19 ? '#f59e0b' : '#475569', fontSize: '0.6rem', fontFamily: 'monospace' }}>{tag.length}/20</span></button>))}</div>)}
          {tab === 'pinterest' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}><span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Pin Title</span><span style={{ fontSize: '0.6rem', color: seo.pinTitle.length >= 95 ? '#f59e0b' : '#475569', fontFamily: 'monospace' }}>{seo.pinTitle.length}/100</span></div>
                <button onClick={() => copySingle(seo.pinTitle, 0)} style={{ width: '100%', background: copied === 'pinterest-0' ? '#14532d' : '#1e293b', border: '1px solid #334155', borderRadius: '0.375rem', padding: '0.5rem 0.625rem', fontSize: '0.75rem', color: copied === 'pinterest-0' ? '#86efac' : '#f8fafc', cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 }}>{seo.pinTitle}</button>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}><span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Pin Description</span><span style={{ fontSize: '0.6rem', color: seo.pinDesc.length >= 480 ? '#f59e0b' : '#475569', fontFamily: 'monospace' }}>{seo.pinDesc.length}/500</span></div>
                <button onClick={() => copySingle(seo.pinDesc, 1)} style={{ width: '100%', background: copied === 'pinterest-1' ? '#14532d' : '#1e293b', border: '1px solid #334155', borderRadius: '0.375rem', padding: '0.5rem 0.625rem', fontSize: '0.75rem', color: copied === 'pinterest-1' ? '#86efac' : '#cbd5e1', cursor: 'pointer', textAlign: 'left', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{seo.pinDesc}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Image Add Input ── */
function ImageAddInput({ onAdd }) {
  const [url, setUrl] = useState('');
  return (
    <div style={{ display: 'flex', gap: '0.375rem' }}>
      <input value={url} onChange={e => setUrl(e.target.value)} placeholder="Paste image URL and press Add" style={{ ...css.input, fontSize: '0.75rem' }} onKeyDown={e => { if (e.key === 'Enter') { onAdd(url); setUrl(''); } }} />
      <button onClick={() => { onAdd(url); setUrl(''); }} style={{ ...css.btn('transparent'), background: '#334155', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>+ Add</button>
    </div>
  );
}

/* ── Variant Card ── */
function VariantCard({ variant, onUpdate, onPublish, onRemove, onRewrite, onRescrapePrice, slData, layout = 'classic' }) {
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [previewImg, setPreviewImg] = useState(null);
  const [imgValidation, setImgValidation] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const [previewDesc, setPreviewDesc] = useState(true);

  const sl = slData || variant._sl || {};
  const slCard = sl.card || {};
  const slDetail = sl.detail || {};
  const slPrecheckData = sl.precheck || {};
  const badges = slCard.badges || [];
  const smartScore = slCard.smartScore;
  const riskTemperature = slCard.riskTemperature;
  const profitTemperature = slCard.profitTemperature;
  const complianceLocked = slCard.complianceLocked || slDetail.complianceLocked || false;
  const finalDecision = slCard.finalDecision;
  const bestNextMove = slCard.bestNextMove;
  const policyLocks = slDetail.policyLocks || [];
  const enrichedDescription = slDetail.enrichedDescription;
  const finalCopyPack = slDetail.finalCopyPack;

  const priceConfirmed = variant.priceConfirmed !== false && variant.price > 0;
  const pricing = (() => { try { return JSON.parse(localStorage.getItem('amorlia_pricing') || '{}'); } catch { return {}; } })();
  const ebayFeePct = parseFloat(pricing.ebayFeePct) || 13.25;
  const shippingCost = parseFloat(pricing.shippingCost) || 4.00;
  const processingPct = parseFloat(pricing.processingPct) || 3.0;
  const minMarginPct = parseFloat(pricing.minMarginPct) || 15;
  const minProfitTarget = parseFloat(pricing.targetProfit) || 5.00;
  const sourcePrice = variant.rawNihaoPrice || 0;
  const sellPrice = variant.price || 0;
  const totalCost = sourcePrice + (sellPrice * ebayFeePct / 100) + 0.30 + (sellPrice * processingPct / 100) + shippingCost;
  const netProfit = sellPrice - totalCost;
  const margin = sellPrice > 0 ? (netProfit / sellPrice) * 100 : 0;
  const marginOk = netProfit >= minProfitTarget;

  const images = variant.images && variant.images.length > 0 ? variant.images : variant.image ? [variant.image] : [];

  const setPrimaryImage = (idx) => { const reordered = [images[idx], ...images.filter((_, i) => i !== idx)]; onUpdate(variant.id, 'images', reordered); onUpdate(variant.id, 'image', reordered[0]); };
  const removeImage = (idx) => { const filtered = images.filter((_, i) => i !== idx); onUpdate(variant.id, 'images', filtered); onUpdate(variant.id, 'image', filtered[0] || ''); };
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); if (dragIdx === null || dragIdx === idx) return; const reordered = [...images]; const [moved] = reordered.splice(dragIdx, 1); reordered.splice(idx, 0, moved); onUpdate(variant.id, 'images', reordered); onUpdate(variant.id, 'image', reordered[0]); setDragIdx(idx); };
  const handleDragEnd = () => setDragIdx(null);
  const addImageUrl = (url) => { if (!url.trim()) return; const updated = [...images, url.trim()].slice(0, 8); onUpdate(variant.id, 'images', updated); if (!images.length) onUpdate(variant.id, 'image', updated[0]); };
  const handlePublish = async () => { setPublishing(true); await onPublish(variant); setPublishing(false); if (onRemove) onRemove(variant.id); };
  const swapToAlt = () => { if (variant.altTitle) { onUpdate(variant.id, 'title', variant.altTitle); onUpdate(variant.id, 'altTitle', variant.title); } };
  const copyToClipboard = (text) => { if (!text) return; navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => { const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); setCopied(true); setTimeout(() => setCopied(false), 2000); }); };  const hasBadImages = Object.values(imgValidation).some(v => v === false);


  const formContent = (

          <>

            {/* Title */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <label style={{ ...css.label, marginBottom: 0 }}>eBay Title</label>
                  {variant.titleValidation && !variant.titleValidation.valid && (<span style={{ fontSize: '0.6rem', background: '#7f1d1d', color: '#fca5a5', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontWeight: 600 }}>TYPE MISMATCH: {variant.titleValidation.warning}</span>)}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem' }}>
                  <button onClick={() => { const nextIdx = ((variant.activeTitleIndex || 0) + 1) % (variant.titleVariants?.length || 1); const updated = swapTitle(variant, nextIdx); onUpdate(variant.id, 'title', updated.title); onUpdate(variant.id, 'activeTitleIndex', updated.activeTitleIndex); }} style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', cursor: 'pointer', fontSize: '0.65rem', padding: '0.125rem 0.5rem', borderRadius: '0.25rem' }}>Swap All {variant.titleVariants?.length > 0 ? `${(variant.activeTitleIndex || 0) + 1}/${variant.titleVariants.length}` : ''}</button>
                  <button onClick={() => copyToClipboard(variant.title)} style={{ background: 'none', border: 'none', color: copied ? '#22c55e' : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem' }}>{copied ? Icon.check(12) : Icon.copy(12)} {copied ? 'Copied' : 'Copy'}</button>
                </div>
              </div>
              <input value={variant.title} onChange={e => onUpdate(variant.id, 'title', e.target.value)} style={{ ...css.input, borderColor: variant.titleValidation && !variant.titleValidation.valid ? '#ef4444' : css.input.borderColor }} maxLength={80} />
              <TitleCounter length={variant.title.length} />
              <KeywordChips title={variant.title} onAppend={chip => onUpdate(variant.id, 'title', (variant.title + ' ' + chip).substring(0, 80))} />
              <TitlePreview variant={variant} />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ ...css.label, marginBottom: 0 }}>Description</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                   <button onClick={() => setPreviewDesc(!previewDesc)} style={{ ...css.btn('transparent'), background: '#1e293b', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '0.375rem' }}>{previewDesc ? 'View HTML Code' : 'Visual Preview'}</button>
                   <button onClick={() => copyToClipboard(variant.description)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem' }}>{Icon.copy(12)} Copy</button>
                </div>
              </div>
              
              {previewDesc ? (
                <div style={{ background: '#fff', color: '#000', padding: '1rem', borderRadius: '0.5rem', maxHeight: '300px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)' }} dangerouslySetInnerHTML={{ __html: variant.description || '<p>No description available.</p>' }} />
              ) : (
                <textarea value={variant.description} onChange={e => onUpdate(variant.id, 'description', e.target.value)} rows={8} style={{ ...css.input, resize: 'vertical', minHeight: '150px', fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: '1.4' }} />
              )}
              
              <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
                {['factual', 'gifting', 'stackable'].map(voice => (
                  <button key={voice} onClick={() => onRewrite && onRewrite(variant, voice)} style={{ flex: 1, padding: '0.375rem 0', background: variant.voice === voice ? (voice === 'gifting' ? '#7c2d12' : voice === 'stackable' ? '#1e3a5f' : '#334155') : '#0f172a', border: `1px solid ${variant.voice === voice ? (voice === 'gifting' ? '#ea580c' : voice === 'stackable' ? '#3b82f6' : '#475569') : '#475569'}`, borderRadius: '0.375rem', color: variant.voice === voice ? (voice === 'gifting' ? '#fb923c' : voice === 'stackable' ? '#60a5fa' : '#94a3b8') : '#94a3b8', fontSize: '0.7rem', cursor: 'pointer', fontWeight: variant.voice === voice ? 700 : 400, textTransform: 'capitalize' }}>{voice}</button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={css.label}>Price ($)</label>
              <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => onUpdate(variant.id, 'price', Math.max(0.99, (variant.price || 0) - 1))} style={{ padding: '0.375rem 0.75rem', background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>-$1</button>
                <input type="number" step="0.01" className="source-price-input" value={variant.price || ''} onChange={e => onUpdate(variant.id, 'price', parseFloat(e.target.value) || 0)} style={{ ...css.input, maxWidth: '120px', textAlign: 'center' }} />
                <button onClick={() => onUpdate(variant.id, 'price', +(variant.price || 0) + 1)} style={{ padding: '0.375rem 0.75rem', background: '#14532d', color: '#86efac', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>+$1</button>
                <button onClick={() => {
                  const source = variant.rawNihaoPrice || 0;
                  if (source > 0) {
                    const pricing = (() => { try { return JSON.parse(localStorage.getItem('amorlia_pricing') || '{}'); } catch { return {}; } })();
                    const ebayPct = parseFloat(pricing.ebayFeePct || 13.25) / 100;
                    const procPct = parseFloat(pricing.processingPct || 3.0) / 100;
                    const ship = parseFloat(pricing.shippingCost || 4.00);
                    const targetProfit = parseFloat(pricing.targetProfit || 5.00);
                    const minPrice = (source + ship + 0.30 + targetProfit) / (1 - ebayPct - procPct);
                    let newPrice = Math.ceil(minPrice);
                    const psych = pricing.pricePsychology || '99';
                    if (psych === '99') newPrice = newPrice - 0.01;
                    else if (psych === '95') newPrice = newPrice - 0.05;
                    onUpdate(variant.id, 'price', Math.max(newPrice, 9.99));
                  }
                }} style={{ padding: '0.375rem 0.5rem', background: '#1e3a5f', color: '#93c5fd', border: '1px solid #3b82f6', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600 }}>REPRICE</button>
                {onRescrapePrice && (<button onClick={onRescrapePrice} style={{ padding: '0.375rem 0.5rem', background: '#422006', color: '#fbbf24', border: '1px solid #f59e0b', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.6rem', fontWeight: 600 }}>RE-SCRAPE PRICE</button>)}
              </div>
            </div>

            <MarginPanel variant={variant} />

            {/* Variant selector — show if product has variants */}
            {variant.variants && variant.variants.length > 0 && (
              <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: 'linear-gradient(135deg, rgba(212,175,55,0.04), rgba(255,255,255,0.02))', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
                  <span style={{ fontSize: '0.7rem', color: '#d4af37', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    ✦ {variant.variants.length} Color Variant{variant.variants.length !== 1 ? 's' : ''}
                  </span>
                  {variant.variants.filter(v => v.price > 0).length > 0 && (
                    <span style={{ fontSize: '0.6rem', color: '#64748b' }}>
                      ${Math.min(...variant.variants.filter(v => v.price > 0).map(v => v.price)).toFixed(2)} – ${Math.max(...variant.variants.filter(v => v.price > 0).map(v => v.price)).toFixed(2)} source
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '0.375rem', maxHeight: '260px', overflowY: 'auto', padding: '2px' }}>
                  {variant.variants.map((v, i) => {
                    const isActive = variant.selectedVariantIndex === i;
                    return (
                      <button
                        key={v.id || i}
                        onClick={() => {
                          onUpdate(variant.id, 'selectedVariantIndex', i);
                          // Update main images to show this variant's images
                          if (v.images && v.images.length > 0) {
                            onUpdate(variant.id, 'images', v.images);
                            onUpdate(variant.id, 'image', v.images[0]);
                          } else if (v.image) {
                            onUpdate(variant.id, 'image', v.image);
                          }
                          // Update source price if this variant has its own price
                          const variantSourcePrice = v.rawNihaoPrice || v.sourcePrice;
                          if (variantSourcePrice && variantSourcePrice > 0) {
                            onUpdate(variant.id, 'rawNihaoPrice', variantSourcePrice);
                          } else if (v.price && v.price > 0 && !v.rawNihaoPrice) {
                            // Fallback only if rawNihaoPrice wasn't explicitly set by the pipeline
                            onUpdate(variant.id, 'rawNihaoPrice', v.price);
                          }
                          
                          // Update eBay price if variant has calculated eBay price
                          const variantEbayPrice = v.ebayPrice || (v.price !== variantSourcePrice ? v.price : null);
                          if (variantEbayPrice && variantEbayPrice > 0) {
                            onUpdate(variant.id, 'price', variantEbayPrice);
                          }
                          // Update Color in item specifics
                          if (v.name) {
                            onUpdate(variant.id, 'itemSpecifics.Color', v.name);
                          }
                        }}
                        title={`${v.title || v.name} — $${v.price?.toFixed(2) || '0.00'} source`}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                          padding: '6px',
                          background: isActive ? 'rgba(212,175,55,0.12)' : 'rgba(0,0,0,0.2)',
                          border: `2px solid ${isActive ? '#d4af37' : 'transparent'}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ width: '48px', height: '48px', borderRadius: '4px', overflow: 'hidden', background: '#1a1a2e' }}>
                          {getVariantImageSrc(v) ? <img src={getVariantImageSrc(v)} alt={v.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} /> : <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#475569', fontSize: '1rem' }}>?</span>}
                        </div>
                        <span style={{ fontSize: '0.55rem', color: isActive ? '#d4af37' : '#94a3b8', textAlign: 'center', lineHeight: 1.2, maxWidth: '65px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(v.name || `#${i + 1}`).slice(0, 12)}
                        </span>
                        {v.price > 0 && <span style={{ fontSize: '0.5rem', color: isActive ? '#86efac' : '#475569' }}>${v.price.toFixed(2)}</span>}
                      </button>
                    );
                  })}
                </div>
                {variant.selectedVariantIndex != null && variant.variants[variant.selectedVariantIndex] && (
                  <div style={{ marginTop: '0.5rem', padding: '0.375rem 0.5rem', background: 'rgba(212,175,55,0.06)', borderRadius: '0.25rem', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Selected: <strong style={{ color: '#d4af37' }}>{variant.variants[variant.selectedVariantIndex].title || variant.variants[variant.selectedVariantIndex].name}</strong></span>
                    <span style={{ display: 'flex', gap: '0.75rem' }}>
                      {variant.variants[variant.selectedVariantIndex].sku && <span>SKU: {variant.variants[variant.selectedVariantIndex].sku}</span>}
                      {variant.variants[variant.selectedVariantIndex].rawNihaoPrice > 0 && <span style={{ color: '#86efac' }}>Source: ${(variant.variants[variant.selectedVariantIndex].rawNihaoPrice || variant.variants[variant.selectedVariantIndex].price).toFixed(2)}</span>}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Images */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ ...css.label, marginBottom: 0 }}>Images ({images.length}/8)</label>
                {hasBadImages && <span style={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 600 }}>Broken image URLs detected</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginBottom: '0.5rem', minHeight: '48px', padding: '0.375rem', background: '#0f172a', borderRadius: '0.375rem', border: '1px solid #1e293b' }}>
                {images.length === 0 && (<span style={{ color: '#475569', fontSize: '0.7rem', alignSelf: 'center' }}>No images. Add a URL below.</span>)}
                {images.map((url, idx) => {
                  const isValid = imgValidation[idx] !== false;
                  return (
                    <div key={idx} draggable onDragStart={() => handleDragStart(idx)} onDragOver={e => handleDragOver(e, idx)} onDragEnd={handleDragEnd} onClick={() => setPrimaryImage(idx)} style={{ width: '48px', height: '48px', borderRadius: '0.25rem', overflow: 'hidden', border: idx === 0 ? '2px solid #3b82f6' : isValid ? '1px solid #334155' : '1px solid #ef4444', cursor: 'grab', position: 'relative', flexShrink: 0, opacity: dragIdx === idx ? 0.4 : 1 }}>
                      <img src={proxyUrl(url)} alt={`img-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.onerror = null; e.target.src = url; }} />
                      <button onClick={e => { e.stopPropagation(); removeImage(idx); }} style={{ position: 'absolute', top: '-2px', right: '-2px', width: '14px', height: '14px', background: '#7f1d1d', border: 'none', borderRadius: '50%', color: '#fca5a5', fontSize: '0.55rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>x</button>
                    </div>
                  );
                })}
              </div>
              <ImageAddInput onAdd={addImageUrl} />
            </div>

            {/* Item Specifics */}
            {variant.itemSpecifics && (() => {
              const specEntries = Object.entries(variant.itemSpecifics);
              const weakFields = specEntries.filter(([, v]) => !v || v === 'Does not apply' || v === 'N/A');
              const hasWarnings = weakFields.length > 0;
              return (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ ...css.label, marginBottom: 0 }}>Item Specifics (Cassini)</label>
                    <span style={{ fontSize: '0.65rem', color: hasWarnings ? '#f59e0b' : '#22c55e', fontWeight: 600 }}>{hasWarnings ? `${weakFields.length} field(s) need attention` : 'All fields populated'}</span>
                  </div>
                  {hasWarnings && (<div style={{ background: '#422006', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', marginBottom: '0.5rem', fontSize: '0.7rem', color: '#fbbf24' }}>These fields improve your Cassini ranking. Fill them before publishing:<ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, listStyle: 'disc' }}>{weakFields.map(([k]) => <li key={k}>{k}</li>)}</ul></div>)}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem' }}>
                    {specEntries.map(([k, v]) => { const isWeak = !v || v === 'Does not apply' || v === 'N/A'; return (<div key={k}><label style={{ fontSize: '0.6rem', color: isWeak ? '#f59e0b' : '#64748b', fontWeight: 500, display: 'block', marginBottom: '0.125rem' }}>{k}</label><input value={v || ''} onChange={e => onUpdate(variant.id, `itemSpecifics.${k}`, e.target.value)} style={{ ...css.input, fontSize: '0.75rem', padding: '0.375rem 0.5rem', borderColor: isWeak ? '#f59e0b' : '#334155' }} /></div>); })}
                  </div>
                </div>
              );
            })()}

            <SEOPanel itemSpecifics={variant.itemSpecifics} />

            {/* Publish */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {!priceConfirmed && (<div style={{ background: '#78350f', borderRadius: '0.375rem', padding: '0.625rem', fontSize: '0.8rem', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>{Icon.eye(14)} Source price not confirmed. Set a price above before publishing.</div>)}
              {priceConfirmed && !marginOk && (<div style={{ background: '#450a0a', borderRadius: '0.375rem', padding: '0.625rem', fontSize: '0.8rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>{Icon.x(14)} Margin below {minMarginPct}%. This listing loses money at current price. Adjust before publishing.</div>)}
              {margin < 0 && (<div style={{ background: '#7f1d1d', borderRadius: '0.375rem', padding: '0.625rem', fontSize: '0.8rem', color: '#fca5a5' }}>Blocked: Negative margin ({margin.toFixed(1)}%). Cannot publish.</div>)}

              {/* SmartLayer policy locks */}
              {policyLocks.length > 0 && (
                <div style={{ background: 'rgba(255,71,87,0.08)', borderRadius: '0.375rem', padding: '0.625rem', border: '1px solid rgba(255,71,87,0.15)' }}>
                  <p style={{ color: '#FF4757', fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>Policy Locks</p>
                  {policyLocks.map((lock, i) => <p key={i} style={{ color: '#fca5a5', fontSize: '0.7rem', margin: 0 }}>{lock.message || lock.id || lock}</p>)}
                </div>
              )}

              {/* SmartLayer final decision */}
              {finalDecision && (
                <div style={{ padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: 6, fontSize: '0.75rem' }}>
                  <span style={{ color: finalDecision.action === 'publish' ? '#2ED573' : finalDecision.action === 'review' ? '#FFA502' : '#FF4757', fontWeight: 700 }}>{finalDecision.label || finalDecision.action}</span>
                  {bestNextMove && <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>{bestNextMove}</span>}
                </div>
              )}

              <button onClick={handlePublish} disabled={publishing || !priceConfirmed || !marginOk || complianceLocked} style={{ ...css.btn('#3b82f6', '#2563eb'), flex: 1, justifyContent: 'center', opacity: publishing || !priceConfirmed || !marginOk || complianceLocked ? 0.4 : 1, cursor: !priceConfirmed || !marginOk || complianceLocked ? 'not-allowed' : 'pointer' }}>
                {Icon.send(16)} {publishing ? 'Publishing...' : complianceLocked ? 'COMPLIANCE LOCKED' : priceConfirmed && marginOk ? 'PUBLISH TO EBAY' : 'ADJUST PRICE TO PUBLISH'}
              </button>

              {/* SmartLayer copy actions */}
              {finalCopyPack && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {finalCopyPack.title && <button onClick={() => copyToClipboard(finalCopyPack.title)} style={{ padding: '4px 8px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 4, color: '#93c5fd', fontSize: '0.65rem', cursor: 'pointer' }}>Copy Final Title</button>}
                  {finalCopyPack.description && <button onClick={() => copyToClipboard(finalCopyPack.description)} style={{ padding: '4px 8px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 4, color: '#93c5fd', fontSize: '0.65rem', cursor: 'pointer' }}>Copy Final Desc</button>}
                  {enrichedDescription && <button onClick={() => copyToClipboard(enrichedDescription)} style={{ padding: '4px 8px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 4, color: '#c4b5fd', fontSize: '0.65rem', cursor: 'pointer' }}>Copy Enriched</button>}
                </div>
              )}

              <button onClick={() => { const ok = saveToCatalog(variant, sl); if(ok) { if(onRemove) onRemove(variant.id); } else { alert('Already in Catalog'); } }} style={{ ...css.btn('#4f46e5', '#4338ca'), flex: 1, justifyContent: 'center' }}>
                {Icon.archive(14)} Save to Catalog
              </button>
            </div>
          </>

  );

  if (layout === 'editorial') {
    return (
      <div style={{ ...css.card, display: 'flex', flexDirection: 'column', height: '100%', padding: 0, border: 'none', background: '#0a0a0f', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
        <div style={{ height: '340px', width: '100%', position: 'relative', background: '#111118' }}>
          {images[0] ? <img src={proxyUrl(images[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e)=>{e.target.onerror=null; e.target.src=images[0];}}/> : <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',color:'#333',fontSize:'2rem'}}>?</div>}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(10,10,15,0.1) 40%, #0a0a0f 100%)' }} />
          <div style={{ position: 'absolute', bottom: '1.25rem', left: '1.5rem', right: '1.5rem' }}>
            <p style={{ color: '#FF6B35', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: '0.375rem' }}>{variant.itemSpecifics?.Style || 'Product Variant'}</p>
            <input value={variant.title} onChange={e => onUpdate(variant.id, 'title', e.target.value)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.25rem', fontWeight: 300, lineHeight: 1.3, width: '100%', outline: 'none', fontFamily: 'serif' }} />
          </div>
          {onRemove && <button onClick={() => onRemove(variant.id)} style={{ position: 'absolute', top: '1rem', right: '1rem', width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>✕</button>}
          <button onClick={() => setExpanded(!expanded)} style={{ position: 'absolute', top: '1rem', right: onRemove ? '3.5rem' : '1rem', width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{expanded ? Icon.x(14) : Icon.edit(14)}</button>
        </div>
        {expanded && <div style={{ padding: '1.5rem' }}>{formContent}</div>}
      </div>
    );
  }

  if (layout === 'compact') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.2s' }}>
        <div style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <div onClick={() => images[0] && setPreviewImg(images[0])} style={{ width: '48px', height: '48px', borderRadius: '0.375rem', background: '#111118', flexShrink: 0, overflow: 'hidden', cursor: images[0] ? 'pointer' : 'default' }}>
            {images[0] && <img src={proxyUrl(images[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
             <input value={variant.title} onChange={e => onUpdate(variant.id, 'title', e.target.value)} style={{ background: 'transparent', border: 'none', color: '#f8fafc', fontSize: '0.875rem', outline: 'none', width: '100%', fontWeight: 500 }} placeholder="Product Title" />
             <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Cost: ${variant.rawNihaoPrice||'0.00'}</span>
             </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
             <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: '0.375rem', padding: '0.25rem 0.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginRight: '0.25rem' }}>$</span>
                <input type="number" step="0.01" value={variant.price || ''} onChange={e => onUpdate(variant.id, 'price', parseFloat(e.target.value)||0)} style={{ background: 'transparent', border: 'none', color: '#2ED573', fontSize: '0.875rem', width: '60px', outline: 'none', fontWeight: 600 }} />
             </div>
             <button onClick={() => setExpanded(!expanded)} style={{ ...css.btn('transparent'), background: '#334155', padding: '0.375rem', borderRadius: '0.375rem' }}>{expanded ? Icon.x(14) : 'Edit'}</button>
             {onRemove && <button onClick={() => onRemove(variant.id)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1rem', padding: '0.25rem', transition: 'color 0.2s' }}>✕</button>}
          </div>
        </div>
        {expanded && <div style={{ padding: '0 1rem 1rem 1rem' }}>{formContent}</div>}
      </div>
    );
  }

  return (
    <div style={css.card}>
      <div style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-start' }}>
          <div onClick={() => images[0] && setPreviewImg(images[0])} style={{ width: '80px', height: '80px', borderRadius: '0.5rem', background: '#334155', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: images[0] ? 'pointer' : 'default' }}>
            {images[0] ? (<img src={proxyUrl(images[0])} alt={variant.variantName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.onerror = null; e.target.src = images[0]; }} />) : (<span style={{ color: '#475569', fontSize: '1.5rem' }}>?</span>)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* SmartLayer badges */}
            {badges.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                {badges.map((b, i) => (
                  <span key={i} style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 9999, background: b.color || '#1e3a5f', color: b.textColor || '#93c5fd', fontWeight: 600 }}>{b.label || b}</span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>{variant.variantName || 'Product Variant'}</p>
                <p style={{ color: '#22c55e', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1 }}>${variant.price}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.375rem' }}>
                <button onClick={() => setExpanded(!expanded)} style={{ ...css.btn('transparent'), background: '#334155', padding: '0.375rem', borderRadius: '0.375rem' }}>{expanded ? Icon.x(14) : Icon.eye(14)}</button>
                {onRemove && (<button onClick={() => onRemove(variant.id)} style={{ ...css.btn('transparent'), background: '#7f1d1d', padding: '0.375rem', borderRadius: '0.375rem' }}>{Icon.x(14)}</button>)}
              </div>
            </div>
            {variant.rawNihaoPrice > 0 && variant.price > 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.7rem', marginTop: '0.25rem' }}>Source: ${variant.rawNihaoPrice} | Margin: {(((variant.price - variant.rawNihaoPrice - (variant.price * 0.1325 + variant.price * 0.03 + 0.35 + 4)) / variant.price) * 100).toFixed(1)}%</p>
            ) : !variant.rawNihaoPrice || variant.rawNihaoPrice <= 0 ? (
              <p style={{ color: '#ef4444', fontSize: '0.7rem', marginTop: '0.25rem', fontWeight: 600 }}>Source price not scraped — enter manually.</p>
            ) : !priceConfirmed ? (
              <p style={{ color: '#f59e0b', fontSize: '0.7rem', marginTop: '0.25rem', fontWeight: 600 }}>Source price not confirmed. Enter manually before publishing.</p>
            ) : null}
            {/* SmartLayer quick chips */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
              {variant.slStatus && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: variant.slStatus === 'READY_TO_REVIEW' ? '#14532d' : variant.slStatus === 'NEEDS_OPTIMIZATION' ? '#78350f' : '#7f1d1d', color: variant.slStatus === 'READY_TO_REVIEW' ? '#86efac' : variant.slStatus === 'NEEDS_OPTIMIZATION' ? '#fbbf24' : '#fca5a5', fontWeight: 600 }}>{variant.slStatus === 'READY_TO_REVIEW' ? 'Cassini Optimized' : variant.slStatus === 'NEEDS_OPTIMIZATION' ? 'Needs Work' : variant.slStatus === 'error' ? 'SL Error' : 'Blocked'}</span>}
              {smartScore != null && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: '#1e3a5f', color: '#93c5fd', fontWeight: 600 }}>Smart: {smartScore}</span>}
              {variant.slConversion?.score?.score != null && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: '#1e3a5f', color: variant.slConversion.score.score >= 80 ? '#86efac' : variant.slConversion.score.score >= 60 ? '#fbbf24' : '#fca5a5', fontWeight: 600 }}>Score: {variant.slConversion.score.score}/100</span>}
              {slPrecheckData.route && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: slPrecheckData.route === 'pass' ? '#14532d' : slPrecheckData.route === 'review' ? '#78350f' : '#7f1d1d', color: slPrecheckData.route === 'pass' ? '#86efac' : slPrecheckData.route === 'review' ? '#fbbf24' : '#fca5a5', fontWeight: 600 }}>Route: {slPrecheckData.route}</span>}
              {riskTemperature && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: riskTemperature === 'hot' ? '#7f1d1d' : riskTemperature === 'warm' ? '#78350f' : '#14532d', color: riskTemperature === 'hot' ? '#fca5a5' : riskTemperature === 'warm' ? '#fbbf24' : '#86efac', fontWeight: 600 }}>Risk: {riskTemperature}</span>}
              {profitTemperature && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: profitTemperature === 'hot' ? '#7f1d1d' : profitTemperature === 'warm' ? '#78350f' : '#14532d', color: profitTemperature === 'hot' ? '#fca5a5' : profitTemperature === 'warm' ? '#fbbf24' : '#86efac', fontWeight: 600 }}>Profit: {profitTemperature}</span>}
              {variant.titleVariants && variant.titleVariants.length > 1 && <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: '#1e3a5f', color: '#93c5fd', fontWeight: 600 }}>{variant.titleVariants.length} title variants</span>}
            </div>
          </div>
        </div>

        {expanded && formContent}

      </div>
      {previewImg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'pointer' }} onClick={() => setPreviewImg(null)}>
          <img src={previewImg} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '0.5rem' }} />
        </div>
      )}
    </div>
  );
}

/* ── Quick Add Form ── */
function QuickAddForm({ onResult }) {
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [image, setImage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${RENDER_API}/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quickAdd: true, product: { title: title.trim(), price: parseFloat(price) || 0, image: image.trim(), description: '', variants: [] } }),
      });
      const data = await res.json();
      if (data.variants) { onResult(data); }
      else if (data.error) { alert('Error: ' + data.error); }
    } catch (err) { alert('Error: ' + err.message); }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '1rem', background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
      <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.75rem', fontWeight: 600 }}>QUICK ADD — paste product details from any supplier</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem', alignItems: 'end' }}>
        <div><label style={css.label}>Product Name</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Stainless Steel Heart Pendant Necklace" style={css.input} required /></div>
        <div><label style={css.label}>Source Price ($)</label><input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="1.50" style={css.input} /></div>
        <div><label style={css.label}>Image URL</label><input value={image} onChange={e => setImage(e.target.value)} placeholder="https://..." style={css.input} /></div>
        <button type="submit" disabled={loading || !title.trim()} style={{ ...css.btn('#22c55e', '#16a34a'), whiteSpace: 'nowrap', opacity: loading || !title.trim() ? 0.6 : 1, height: '38px' }}>
          {Icon.send(14)} {loading ? '...' : 'ADD'}
        </button>
      </div>
    </form>
  );
}

/* ── Variant Preview Panel ── */
function VariantPreviewPanel({ scrapeData, variants, onToggleVariant }) {
  const [expanded, setExpanded] = useState(true);
  const sourceVariants = scrapeData.rawScrapeData?.variants || scrapeData.variants?.[0]?.variants || scrapeData.variants || [];
  if (!sourceVariants || sourceVariants.length === 0) return null;
  const priceRange = scrapeData.variantPriceRange;
  const hasDifferential = scrapeData.hasPriceDifferential;
  const includedIds = new Set(variants.map(v => v.id));
  return (
    <div className="glass-card" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: expanded ? '0.75rem' : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600 }}>Source Variants ({sourceVariants.length})</h3>
          {hasDifferential && (<span style={{ fontSize: '0.6rem', background: '#422006', color: '#fbbf24', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontWeight: 600 }}>PRICE RANGE</span>)}
        </div>
        <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>{expanded ? Icon.x(14) : Icon.eye(14)}</button>
      </div>
      {expanded && (
        <>
          {hasDifferential && priceRange && (
            <div style={{ background: '#422006', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.7rem', color: '#fbbf24' }}>
              Different variants have different source prices:
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.25rem' }}>
                {priceRange.prices.map((p, i) => (<span key={i} style={{ background: '#334155', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontFamily: 'monospace' }}>{p.name}: ${p.price}</span>))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {sourceVariants.map((v, i) => {
              const isIncluded = includedIds.has(v.id);
              const isOutOfStock = v.stock === false;
              const hasImage = v.image && v.image.startsWith('http');
              return (
                <div key={v.id || i} onClick={() => !isOutOfStock && onToggleVariant && onToggleVariant(v, !isIncluded)} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', borderRadius: '9999px', border: `1px solid ${isOutOfStock ? '#334155' : isIncluded ? '#3b82f6' : '#475569'}`, background: isOutOfStock ? '#0f172a' : isIncluded ? 'rgba(59,130,246,0.15)' : '#0f172a', cursor: isOutOfStock ? 'not-allowed' : 'pointer', opacity: isOutOfStock ? 0.4 : 1, textDecoration: isOutOfStock ? 'line-through' : 'none', transition: 'all 0.15s', position: 'relative' }}>
                  {hasImage ? (<img src={proxyUrl(v.image)} alt={v.name} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} onError={e => { e.target.style.display = 'none'; }} />) : (<div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', color: '#64748b' }}>?</div>)}
                  <span style={{ fontSize: '0.75rem', color: isOutOfStock ? '#475569' : isIncluded ? '#93c5fd' : '#94a3b8', fontWeight: isIncluded ? 600 : 400 }}>{v.name}</span>
                  {v.price && v.price > 0 && (<span style={{ fontSize: '0.6rem', color: '#fbbf24', fontFamily: 'monospace' }}>${v.price}</span>)}
                  {isOutOfStock && (<span style={{ fontSize: '0.5rem', color: '#ef4444', fontWeight: 600, position: 'absolute', top: '-4px', right: '-2px', background: '#7f1d1d', borderRadius: '9999px', padding: '0 3px' }}>OOS</span>)}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Batch Import Banner ── */
function BatchImportBanner({ results, onDismiss }) {
  const successCount = results.filter(r => r.success).length;
  return (
    <div style={{ background: 'linear-gradient(135deg, #14532d, #1e3a5f)', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', border: '1px solid rgba(46,213,115,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {Icon.batch(20)}
        <div>
          <p style={{ fontWeight: 700, color: '#2ED573' }}>Bookmarklet Import Complete</p>
          <p style={{ color: '#86efac', fontSize: '0.8rem' }}>{successCount} product{successCount !== 1 ? 's' : ''} imported from Nihao and ready to edit below.</p>
        </div>
      </div>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>{Icon.x(16)}</button>
    </div>
  );
}

/* ── SmartLayer already-processed check ── */
function isAlreadySmartLayerProcessed(product) {
  if (!product || typeof product !== 'object') return false;
  return !!(
    product.slStatus ||
    product.slOk != null ||
    product.score != null ||
    product.pricing != null ||
    product.publishGate != null ||
    (product.titleVariants && product.titleVariants.length > 0) ||
    product.orderEntry != null ||
    product.smartLayerProcessed
  );
}

/* ── Layout Switcher ── */
function LayoutSwitcher({ activeLayout, onLayoutChange }) {
  const options = [
    { id: 'classic', label: 'Classic Grid', icon: '⊞' },
    { id: 'editorial', label: 'Editorial', icon: '🖼' },
    { id: 'compact', label: 'Compact List', icon: '☰' },
    { id: 'split', label: 'Split Pane', icon: '◫' },
    { id: 'swipe', label: 'Swipe Cards', icon: '📱' },
  ];
  return (
    <div style={{ display: 'flex', gap: '0.375rem', padding: '0.375rem', background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(10px)', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.08)', width: 'fit-content', overflowX: 'auto', maxWidth: '100%' }}>
      {options.map(o => (
        <button key={o.id} onClick={() => onLayoutChange(o.id)} style={{ padding: '0.5rem 0.875rem', background: activeLayout === o.id ? 'linear-gradient(135deg, rgba(255,107,53,0.2), rgba(255,107,53,0.05))' : 'transparent', color: activeLayout === o.id ? '#FF6B35' : '#94a3b8', border: `1px solid ${activeLayout === o.id ? 'rgba(255,107,53,0.3)' : 'transparent'}`, borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: activeLayout === o.id ? 600 : 500, display: 'flex', alignItems: 'center', gap: '0.375rem', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '0.9rem' }}>{o.icon}</span> {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── SplitPaneLayout ── */
function SplitPaneLayout({ variants, slData, updateVariant, publishVariant, removeVariant, rewriteVariant, handleRescrapePrice }) {
  const [selectedId, setSelectedId] = useState(variants[0]?.id);
  useEffect(() => { if (!variants.find(v => v.id === selectedId) && variants.length > 0) setSelectedId(variants[0].id); }, [variants, selectedId]);
  const activeVariant = variants.find(v => v.id === selectedId);

  return (
    <div style={{ display: 'flex', gap: '1.5rem', height: '80vh', minHeight: '600px' }}>
      <div style={{ width: '320px', display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.02)', borderRadius: '1rem', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 600, color: '#f8fafc' }}>Variants ({variants.length})</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {variants.map(v => {
            const isSelected = v.id === selectedId;
            const img = v.images?.[0] || v.image;
            return (
              <div key={v.id} onClick={() => setSelectedId(v.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', cursor: 'pointer', background: isSelected ? 'rgba(255,107,53,0.1)' : 'transparent', borderLeft: `3px solid ${isSelected ? '#FF6B35' : 'transparent'}`, transition: 'background 0.2s' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '0.25rem', background: '#111118', flexShrink: 0, overflow: 'hidden' }}>
                  {img && <img src={proxyUrl(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: isSelected ? '#f8fafc' : '#cbd5e1', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.title || 'Untitled'}</p>
                  <p style={{ color: '#2ED573', fontSize: '0.7rem', fontWeight: 600 }}>${v.price || '0.00'}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: 'rgba(255,255,255,0.02)', borderRadius: '1rem', border: '1px solid rgba(255,255,255,0.08)' }}>
        {activeVariant ? (
          <VariantCard variant={activeVariant} slData={slData[activeVariant.id]} onUpdate={updateVariant} onPublish={publishVariant} onRemove={removeVariant} onRewrite={rewriteVariant} onRescrapePrice={handleRescrapePrice} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>Select a variant to edit</div>
        )}
      </div>
    </div>
  );
}

/* ── Extract Page ── */
function ExtractPage() {
  const {
    extractQueue: variants,
    setExtractQueue,
    slData,
    setSlData,
    isImporting,
    setIsImporting,
    importSourceInfo: contextSourceInfo,
    setImportSourceInfo,
    updateVariant: contextUpdateVariant,
    removeVariant: contextRemoveVariant,
    handleIncomingProducts,
    savePageDraft,
    getPageDraft,
  } = useAmorliaDashboard();

  const [url, setUrl] = useState(() => getPageDraft('extract', 'url', ''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [swipeIdx, setSwipeIdx] = useState(() => getPageDraft('extract', 'swipeIdx', 0));
  const [layoutMode, setLayoutMode] = useState(() => getPageDraft('extract', 'layoutMode', 'classic'));
  const isMobile = useIsMobile();
  const [sourceInfo, setSourceInfo] = useState('');
  const [rawScrapeData, setRawScrapeData] = useState(null);
  const [showRawPanel, setShowRawPanel] = useState(false);
  const [cacheBadge, setCacheBadge] = useState(null);
  const [priceWarning, setPriceWarning] = useState(null);
  const [showScrapeHistory, setShowScrapeHistory] = useState(() => getPageDraft('extract', 'showScrapeHistory', false));
  const [lastScrapeResponse, setLastScrapeResponse] = useState(null);
  const [batchResults, setBatchResults] = useState(null);
  const [splitVariants, setSplitVariants] = useState(() => getPageDraft('extract', 'splitVariants', false));
  const [scrapeHistory, setScrapeHistory] = useState(() => { try { return JSON.parse(localStorage.getItem('amorlia_scrape_history') || '[]'); } catch { return []; } });
  const lastScrapeUrl = useRef('');
  const [recentPrices, setRecentPrices] = useState(() => { try { return JSON.parse(localStorage.getItem('amorlia_recent_prices') || '[]'); } catch { return []; } });

  const slSettings = loadSmartLayerSettings();

  // Save draft state on changes
  useEffect(() => { savePageDraft('extract', 'url', url); }, [url, savePageDraft]);
  useEffect(() => { savePageDraft('extract', 'swipeIdx', swipeIdx); }, [swipeIdx, savePageDraft]);
  useEffect(() => { savePageDraft('extract', 'layoutMode', layoutMode); }, [layoutMode, savePageDraft]);
  useEffect(() => { savePageDraft('extract', 'showScrapeHistory', showScrapeHistory); }, [showScrapeHistory, savePageDraft]);
  useEffect(() => { savePageDraft('extract', 'splitVariants', splitVariants); }, [splitVariants, savePageDraft]);

  // Use contextSourceInfo when context has import info, otherwise use local
  const displaySourceInfo = sourceInfo || contextSourceInfo;

  const saveRecentPrice = (p) => { if (!p || p <= 0) return; const updated = [p, ...recentPrices.filter(rp => rp !== p)].slice(0, 10); setRecentPrices(updated); localStorage.setItem('amorlia_recent_prices', JSON.stringify(updated)); };

  // ── Batch import via job ID from URL ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jobId = params.get('job');
    if (!jobId) return;

    window.history.replaceState({}, '', window.location.pathname);

    fetch(`${RENDER_API}/batch-result?job=${jobId}`)
      .then(r => r.json())
      .then(async data => {
        if (!data.results) return;
        const successful = data.results.filter(r => r.success).map(r => r.data);
        if (successful.length === 0) return;
        const allVariants = successful.flatMap(d =>
          (d.variants || []).map(v => ({ ...v, rawNihaoPrice: d.rawNihaoPrice }))
        );
        if (allVariants.length > 0) {
          const processed = await processBatch(allVariants);
          const slMap = {};
          processed.forEach(v => { if (v._sl) slMap[v.id] = v._sl; });
          setSlData(prev => ({ ...prev, ...slMap }));
          setExtractQueue(processed);
          setBatchResults(data.results);
          setSourceInfo(`Batch import: ${successful.length} product(s) loaded from Nihao via extension.`);
        }
      })
      .catch(err => console.error('Batch fetch failed:', err));
  }, []);

  // Persist import queue to localStorage (handled by context auto-save)
  useEffect(() => { if (priceWarning && variants.length > 0) { setTimeout(() => { document.querySelector('.source-price-input')?.focus(); }, 300); } }, [priceWarning, variants]);

  // ── Extension listeners are now global (in AmorliaDashboardContext) ──
  // Products received on any page go to the global extractQueue.

  // ── Scrape helpers ──
  const getCacheKey = (u) => { let h = 0; for (let i = 0; i < u.length; i++) { h = ((h << 5) - h) + u.charCodeAt(i); h |= 0; } return h.toString(36); };
  const getCachedScrape = (u) => { try { const cache = JSON.parse(localStorage.getItem('amorlia_scrape_cache') || '{}'); const entry = cache[getCacheKey(u)]; if (!entry) return null; const age = Date.now() - entry.timestamp; if (age > 24 * 60 * 60 * 1000) { const cache2 = { ...cache }; delete cache2[getCacheKey(u)]; localStorage.setItem('amorlia_scrape_cache', JSON.stringify(cache2)); return null; } return entry; } catch { return null; } };
  const setCachedScrape = (u, data) => { try { const cache = JSON.parse(localStorage.getItem('amorlia_scrape_cache') || '{}'); cache[getCacheKey(u)] = { ...data, timestamp: Date.now() }; localStorage.setItem('amorlia_scrape_cache', JSON.stringify(cache)); } catch {} };
  const logScrapeHistory = (scrapeUrl, duration, status, resultSnippet) => { try { const history = JSON.parse(localStorage.getItem('amorlia_scrape_history') || '[]'); history.unshift({ url: scrapeUrl, timestamp: new Date().toISOString(), duration, status, snippet: resultSnippet }); if (history.length > 100) history.length = 100; localStorage.setItem('amorlia_scrape_history', JSON.stringify(history)); setScrapeHistory(history); } catch {} };
  const getSettings = () => { try { return JSON.parse(localStorage.getItem('amorlia_settings') || '{}'); } catch { return {}; } };

  const handleBatchPaste = async (urlList) => {
    setLoading(true);
    const res = await fetch(`${RENDER_API}/batch-scrape`, { 
      method: "POST", 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ urls: urlList }) 
    });
    const { jobId } = await res.json();

    const poll = setInterval(async () => {
      const r = await fetch(`${RENDER_API}/batch-result?job=${jobId}`);
      const data = await r.json();
      if (data.results) {
        clearInterval(poll);
        setLoading(false);
        const successfulProducts = data.results.filter(item => item.success).map(item => item.data);
        
        const allVariants = [];
        successfulProducts.forEach(product => {
          if (product.variants) {
            product.variants.forEach(v => {
              allVariants.push({
                ...v,
                rawNihaoPrice: product.rawNihaoPrice,
                id: v.id || 'batch_' + Date.now() + '_' + Math.random().toString(36).slice(2,6)
              });
            });
          }
        });

        if (allVariants.length > 0) {
          const enriched = await processBatch(allVariants);
          const slMap = {};
          enriched.forEach(v => { if (v._sl) slMap[v.id] = v._sl; });
          setSlData(prev => ({ ...prev, ...slMap }));
          setExtractQueue(enriched);
          setSourceInfo(`Batch scraped ${successfulProducts.length} products. Added ${allVariants.length} variants to queue.`);
        }
      }
    }, 1500);
  };

  const handleScrape = async (forceRefresh = false) => {
    if (!url.trim()) return;
    const urls = url.split(/[\n,]+/).map(u => u.trim()).filter(u => u.startsWith('http'));
    if (urls.length === 0) return setError('Please enter valid URLs starting with http');
    
    if (urls.length > 1) {
      handleBatchPaste(urls);
      setUrl('');
      return;
    }
    
    setLoading(true);
    setError('');
    setSourceInfo('');
    setPriceWarning(null);
    setBatchResults(null);
    
    const settings = getSettings();
    const scrapeTimeout = parseInt(settings.scrapeTimeout) || 30;

    let successCount = 0;
    
    for (const singleUrl of urls) {
      lastScrapeUrl.current = singleUrl;
      const startTime = Date.now();

      if (!forceRefresh) {
        const cached = getCachedScrape(singleUrl);
        if (cached && urls.length === 1) { 
          const age = Math.round((Date.now() - cached.timestamp) / 60000); 
          setCacheBadge({ time: `${age}m ago`, data: cached.data }); 
          setLoading(false); 
          return; 
        }
      }
      if (urls.length === 1) setCacheBadge(null);

      try {
        setSourceInfo(urls.length > 1 ? `Scraping ${successCount + 1} of ${urls.length}...` : 'Scraping...');
        const res = await fetch(`${RENDER_API}/scrape`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: singleUrl, scrapeTimeout: scrapeTimeout * 1000 }),
        });
        const data = await res.json();
        const duration = Date.now() - startTime;

        if (data.error) {
          if (urls.length === 1) setError(data.error + (data.hint ? ' ' + data.hint : ''));
          logScrapeHistory(singleUrl, duration, 'error', data.error);
        } else {
          if (urls.length === 1) {
            setRawScrapeData(data.rawScrapeData || data);
            setLastScrapeResponse(data);
          }
          setCachedScrape(singleUrl, { data });
          if (urls.length === 1 && data.scrapeMeta?.priceWarning) setPriceWarning(data.scrapeMeta.priceWarning);
          
          if (data.variants && data.variants.length > 0) {
            if (splitVariants) {
              const splitProducts = data.variants.map((v, idx) => {
                const vSource = v.rawNihaoPrice || v.sourcePrice || v.price;
                return {
                  ...data,
                  id: v.id || 'var_' + Date.now() + '_' + idx,
                  title: `${data.title || 'Product'} - ${v.name || v.color || 'Variant'}`,
                  price: vSource && vSource > 0 ? vSource : data.rawNihaoPrice || data.price,
                  image: v.image || data.image,
                  images: v.image ? [v.image, ...(data.images || []).filter(img => img !== v.image)].slice(0, 8) : data.images,
                  variants: [{ ...v, id: '1' }]
                };
              });
              const enriched = await processBatch(splitProducts);
              const slMap = {};
              enriched.forEach(v => { if (v._sl) slMap[v.id] = v._sl; });
              setSlData(prev => ({ ...prev, ...slMap }));
              setExtractQueue(prev => [...prev, ...enriched]); // append for bulk
            } else {
              // Group variants: process the main product with variants
              const enriched = await processSingleProduct(data);
              setExtractQueue(prev => [...prev, enriched]);
              if (enriched._sl) setSlData(prev => ({ ...prev, [enriched.id]: enriched._sl }));
            }
            
            successCount++;
            if (urls.length === 1) {
              const priceSourceLabel = data.scrapeMeta?.priceSource ? ` | Price via: ${data.scrapeMeta.priceSource}` : '';
              const priceLine = data.sourcePriceConfirmed
                ? `Source price: $${data.rawNihaoPrice} | eBay price: $${data.calculatedPrice} | Variants: ${data.variants.length}${data.scrapeMeta ? ` | Source: ${data.scrapeMeta.source}` : ''}${priceSourceLabel}`
                : `Source price: not confirmed | Variants: ${data.variants.length} | Set price manually before publishing${priceSourceLabel}`;
              setSourceInfo(priceLine);
            }
            logScrapeHistory(singleUrl, duration, 'success', data.variants?.[0]?.title?.substring(0, 60) || 'OK');
          } else {
            logScrapeHistory(singleUrl, duration, 'no-variants', 'No variants generated');
          }
        }
      } catch (err) {
        const duration = Date.now() - startTime;
        if (urls.length === 1) setError('Network error: ' + err.message);
        logScrapeHistory(singleUrl, duration, 'network-error', err.message);
      }
    }
    
    if (urls.length > 1) {
      setSourceInfo(`Bulk scrape complete: ${successCount}/${urls.length} successful`);
      setUrl(''); // clear textarea after successful bulk
    }
    setLoading(false);
  };

  // Re-scrape price by calling main scrape endpoint and re-running pipeline
  const handleRescrapePrice = async () => {
    if (!lastScrapeUrl.current) return;
    try {
      const res = await fetch(`${RENDER_API}/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: lastScrapeUrl.current, scrapeTimeout: 20000 }),
      });
      const data = await res.json();
      if (data.error) { setError('Price re-scrape failed: ' + data.error); return; }
      const sourcePrice = data.rawNihaoPrice || 0;
      const priceSource = data.scrapeMeta?.priceSource || 'unknown';
      if (sourcePrice > 0) {
        const calculatePrice = (nihaoPrice) => {
          const shipping = 4.00;
          const ebayFeeRate = 0.1325;
          const processingFee = 0.30;
          const processingPct = 0.03;
          const targetProfit = 5.00;
          const minPrice = (nihaoPrice + shipping + processingFee + targetProfit) / (1 - ebayFeeRate - processingPct);
          let price = Math.ceil(minPrice);
          if (price < 30) price = price - 0.01;
          else if (price > 75) price = Math.ceil(minPrice);
          else price = price - 0.01;
          return Math.max(price, 9.99);
        };
        const ebayPrice = calculatePrice(sourcePrice);
        // Re-run pipeline with updated price for all variants
        const prevVariants = variants;
        const reprocessed = await Promise.all(prevVariants.map(async v => {
          const updated = { ...v, rawNihaoPrice: sourcePrice, price: ebayPrice, priceConfirmed: true };
          try {
            return await reprocessProduct(updated, sourcePrice);
          } catch (_) {
            return updated;
          }
        }));
        setExtractQueue(reprocessed);
        setSourceInfo(`Price updated: $${sourcePrice.toFixed(2)} via ${priceSource} → $${ebayPrice.toFixed(2)}`);
        setPriceWarning(null);
      } else {
        setPriceWarning('Price extraction failed. Enter price manually.');
      }
    } catch (err) {
      setError('Price re-scrape failed: ' + err.message);
    }
  };

  const handleQuickAddResult = async (data) => {
    const withIds = data.variants.map(v => ({ ...v, rawNihaoPrice: data.rawNihaoPrice, id: v.id || 'qa_' + Date.now() + '_' + Math.random().toString(36).slice(2,6) }));
    const enriched = await processBatch(withIds);
    const slMap = {};
    enriched.forEach(v => { if (v._sl) slMap[v.id] = v._sl; });
    setSlData(prev => ({ ...prev, ...slMap }));
    setExtractQueue(enriched);
    const priceLine = data.sourcePriceConfirmed
      ? `Source price: $${data.rawNihaoPrice} | eBay price: $${data.calculatedPrice} | Variants: ${data.variants.length}`
      : `Source price: $${data.rawNihaoPrice || 'not set'} | Variants: ${data.variants.length} | Set price manually if needed`;
    setSourceInfo(priceLine);
    setError('');
    if (data.scrapeMeta?.priceWarning) setPriceWarning(data.scrapeMeta.priceWarning);
    setBatchResults(null);
  };

  const handleManual = async () => {
    const manual = { id: 'manual_' + Date.now() + '_' + Math.random().toString(36).slice(2,6), variantName: 'Manual Entry', title: '', description: '', price: 14.99, image: '', images: [], stock: true, itemSpecifics: { Brand: 'Unbranded', 'Main Stone': 'No Stone', 'Base Metal': 'Stainless Steel', Metal: 'Gold Tone', 'Metal Purity': 'Not Solid', Style: 'Pendant', Theme: 'Fashion, Gift', Type: 'Necklace', Color: 'Gold', Occasion: 'Casual, Gift', Clasp: 'Lobster Claw', 'Chain Type': 'Box Chain', Length: '18 in', 'Country/Region of Manufacture': 'China', 'Seller Warranty': '30 Day Returns', Department: 'Women', 'Tarnish Resistant': 'Yes' } };
    const enriched = await processBatch([manual]);
    const slMap = {};
    enriched.forEach(v => { if (v._sl) slMap[v.id] = v._sl; });
    setSlData(prev => ({ ...prev, ...slMap }));
    setExtractQueue(enriched);
    setError('');
    setSourceInfo('Manual entry mode');
    setPriceWarning(null);
    setBatchResults(null);
  };

  const handleToggleVariant = (sourceVariant, include) => {
    if (include) {
      const response = lastScrapeResponse;
      if (!response) return;
      const existing = response.variants.find(v => v.id === sourceVariant.id);
      if (!existing) return;
      fetch(`${RENDER_API}/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quickAdd: true, product: { title: existing.name || sourceVariant.name, price: existing.variantSourcePrice || sourceVariant.price || response.rawNihaoPrice || 0, image: existing.image || sourceVariant.image || '', description: '', variants: [] } }),
      }).then(r => r.json()).then(async data => {
        if (data.variants && data.variants[0]) {
          const v = data.variants[0];
          const raw = { ...v, id: sourceVariant.id, variantName: sourceVariant.name, variantType: sourceVariant.type || 'color', rawNihaoPrice: existing.variantSourcePrice || sourceVariant.price || response.rawNihaoPrice, stock: sourceVariant.stock !== false, image: sourceVariant.image || v.image };
          const processed = await processSingleProduct(raw);
          if (processed._sl) setSlData(prev => ({ ...prev, [processed.id]: processed._sl }));
          setExtractQueue(prev => [...prev, processed]);
        }
      }).catch(() => {});
    } else {
      setExtractQueue(prev => prev.filter(v => v.id !== sourceVariant.id));
    }
  };

  const updateVariant = (id, field, value) => {
    setExtractQueue(prev => prev.map(v => {
      if (v.id !== id) return v;
      if (field.startsWith('itemSpecifics.')) { const key = field.replace('itemSpecifics.', ''); return { ...v, itemSpecifics: { ...v.itemSpecifics, [key]: value } }; }
      return { ...v, [field]: value };
    }));
    if (field === 'price' && value > 0) saveRecentPrice(value);
  };

  const rewriteVariant = async (variant, voice) => {
    // Try SmartLayer platform switch first (instant, no network)
    const platformMap = { factual: 'ebay', gifting: 'etsy', stackable: 'shopify' };
    const targetPlatform = platformMap[voice];
    if (targetPlatform && variant.slPlatforms && variant.slPlatforms[targetPlatform]) {
      const switched = switchPlatform(variant, targetPlatform);
      if (switched) {
        setExtractQueue(prev => prev.map(v => v.id === variant.id ? switched : v));
        return;
      }
    }
    // Fallback to server-side rewrite
    try {
      const res = await fetch(`${RENDER_API}/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rewrite: true, voice, product: { title: variant.variantName || variant.title, price: variant.rawNihaoPrice || 0, image: variant.image, description: '', variants: [] } }),
      });
      const data = await res.json();
      if (data.variants && data.variants[0]) {
        const updated = data.variants[0];
        setExtractQueue(prev => prev.map(v => v.id === variant.id ? { ...v, description: updated.description, voice: updated.voice } : v));
      }
    } catch (err) { console.error('Rewrite failed:', err); }
  };

  const removeVariant = (id) => {
    if (id == null) return;
    setExtractQueue(prev => prev.filter(v => v.id !== id));
  };

  const publishVariant = async (variant) => {
    alert('eBay publisher is not yet configured on Render. To enable publishing, add a /publish endpoint to your backend with your eBay credentials. For now, copy the listing data manually.');
  };

  return (
    <div>
      <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}>
        <h2 style={{ fontSize: isMobile ? '22px' : '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Extract & Generate</h2>
        <p style={{ color: '#94a3b8', fontSize: isMobile ? '15px' : '0.875rem' }}>Paste a product URL from Nihao Jewelry or any supplier to auto-generate eBay listings.</p>
      </div>

      <div className="glass-card" style={{ padding: isMobile ? '16px' : '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <textarea 
              value={url} 
              onChange={e => { setUrl(e.target.value); setCacheBadge(null); }} 
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleScrape(); } }} 
              placeholder="Paste one or multiple Nihao URLs here (one per line)..." 
              style={{ ...css.input, resize: 'none', height: url.split('\n').length > 1 ? Math.min(120, url.split('\n').length * 24 + 16) + 'px' : '80px', overflowY: 'auto' }} 
              disabled={loading} 
            />
            {cacheBadge && url.split('\n').length === 1 && (
              <div style={{ position: 'absolute', right: '8px', top: '10px', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <span style={{ fontSize: '0.65rem', background: '#14532d', color: '#86efac', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontWeight: 600 }}>Cached {cacheBadge.time}</span>
                <button onClick={() => handleScrape(true)} style={{ fontSize: '0.6rem', background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '0.25rem', padding: '0.125rem 0.375rem', cursor: 'pointer' }}>Force</button>
              </div>
            )}
          </div>
          <button onClick={() => handleScrape(false)} disabled={loading || !url.trim()} style={{ ...css.btn('#FF6B35', '#E85A25'), whiteSpace: 'nowrap', opacity: loading || !url.trim() ? 0.6 : 1 }}>
            {Icon.extract(16)} {loading ? 'Extracting...' : 'EXTRACT'}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleManual} style={{ ...css.btn('transparent'), background: '#334155', color: '#94a3b8', fontSize: '0.8rem', padding: '0.5rem 0.875rem' }}>{Icon.edit(14)} Manual Entry</button>
            <button onClick={() => setShowScrapeHistory(!showScrapeHistory)} style={{ ...css.btn('transparent'), background: '#334155', color: '#94a3b8', fontSize: '0.8rem', padding: '0.5rem 0.875rem' }}>{Icon.monitor(14)} History ({scrapeHistory.length})</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer', background: '#334155', color: '#94a3b8', padding: '0.5rem 0.875rem', borderRadius: '0.5rem', fontSize: '0.8rem' }}>
              <input type="checkbox" checked={splitVariants} onChange={() => setSplitVariants(!splitVariants)} style={{ accentColor: '#FF6B35' }} />
              <span>Split Variants</span>
            </label>
          </div>
          {loading && (<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}><div style={{ width: '16px', height: '16px', border: '2px solid #334155', borderTopColor: '#e11d48', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />Scraping product data...</div>)}
        </div>
      </div>

      {/* Batch import banner */}
      {batchResults && <BatchImportBanner results={batchResults} onDismiss={() => setBatchResults(null)} />}

      {/* Price Status Banner */}
      {priceWarning ? (
        <div style={{ background: '#78350f', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', fontSize: '0.875rem', color: '#fbbf24', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <span>{priceWarning}</span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {lastScrapeUrl.current && lastScrapeUrl.current.includes('nihaojewelry') && (<a href={lastScrapeUrl.current} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#fbbf24', textDecoration: 'underline', whiteSpace: 'nowrap' }}>Open on Nihao →</a>)}
            <button onClick={handleRescrapePrice} style={{ ...css.btn('#FF6B35', '#E85A25'), fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}>Re-scrape Price</button>
          </div>
        </div>
      ) : variants.length > 0 && lastScrapeResponse?.scrapeMeta?.priceDetected && (
        <div style={{ background: '#14532d', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.8rem', color: '#86efac', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Price detected via: <strong>{lastScrapeResponse.scrapeMeta.priceSource}</strong></span>
          <span>Source: ${lastScrapeResponse.rawNihaoPrice?.toFixed(2)}</span>
        </div>
      )}

      {/* Recent Prices */}
      {priceWarning && recentPrices.length > 0 && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Recent prices:</span>
          {recentPrices.map((p) => (<button key={p} onClick={() => { if (variants.length > 0) updateVariant(variants[0].id, 'price', p); }} style={{ padding: '0.25rem 0.625rem', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: '9999px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>${p}</button>))}
        </div>
      )}

      {/* Scrape History */}
      {showScrapeHistory && (
        <div className="glass-card" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600 }}>Scrape History (last 100)</h3>
            <button onClick={() => setShowScrapeHistory(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>{Icon.x(16)}</button>
          </div>
          {scrapeHistory.length === 0 ? (<p style={{ color: '#475569', fontSize: '0.8rem' }}>No scrape history yet.</p>) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {scrapeHistory.map((entry, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '0.75rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.url}</p>
                    <p style={{ color: '#475569', fontSize: '0.65rem' }}>{new Date(entry.timestamp).toLocaleString()} | {entry.duration ? `${(entry.duration / 1000).toFixed(1)}s` : '—'}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginLeft: '1rem' }}>
                    <span style={{ fontSize: '0.65rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontWeight: 600, background: entry.status === 'success' ? '#14532d' : entry.status === 'error' ? '#7f1d1d' : '#422006', color: entry.status === 'success' ? '#86efac' : entry.status === 'error' ? '#fca5a5' : '#fbbf24' }}>{entry.status}</span>
                    {entry.snippet && <span style={{ color: '#64748b', fontSize: '0.6rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.snippet}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <QuickAddForm onResult={handleQuickAddResult} />
      </div>

      {error && (<div style={{ background: '#7f1d1d', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', fontSize: '0.875rem' }}><strong>Error:</strong> {error}</div>)}
      {isImporting && (<div style={{ background: '#78350f', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.8rem', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>&#9881;</span> Processing products through SmartLayer...</div>)}
      {displaySourceInfo && (<div style={{ background: '#1e3a5f', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.8rem', color: '#93c5fd' }}>{displaySourceInfo}</div>)}

      {/* Raw Scrape Debug Panel */}
      {rawScrapeData && (
        <div style={{ marginBottom: '1.5rem' }}>
          <button onClick={() => setShowRawPanel(!showRawPanel)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: showRawPanel ? '0.5rem' : 0 }}>
            {showRawPanel ? Icon.x(12) : Icon.eye(12)} {showRawPanel ? 'Hide' : 'Show'} Raw Scrape Data
          </button>
          {showRawPanel && (<pre style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '1rem', fontSize: '0.7rem', color: '#94a3b8', maxHeight: '300px', overflow: 'auto', border: '1px solid #1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(rawScrapeData, null, 2)}</pre>)}
        </div>
      )}

      {lastScrapeResponse && (<VariantPreviewPanel scrapeData={lastScrapeResponse} variants={variants} onToggleVariant={handleToggleVariant} />)}

      {variants.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Product Queue ({variants.length})</h3>
          {!isMobile && <LayoutSwitcher activeLayout={layoutMode} onLayoutChange={setLayoutMode} />}
        </div>
      )}

      {variants.length > 0 && (
        isMobile || layoutMode === 'swipe' ? (
          <SwipeNavigator currentIndex={swipeIdx} onIndexChange={setSwipeIdx}>
            {variants.map(v => (<VariantCard key={v.id} variant={v} layout="classic" slData={slData[v.id]} onUpdate={updateVariant} onPublish={publishVariant} onRemove={(id) => { removeVariant(id); if (swipeIdx >= variants.length - 1) setSwipeIdx(Math.max(0, variants.length - 2)); }} onRewrite={rewriteVariant} onRescrapePrice={handleRescrapePrice} />))}
          </SwipeNavigator>
        ) : layoutMode === 'editorial' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {variants.map(v => (<VariantCard key={v.id} variant={v} layout="editorial" slData={slData[v.id]} onUpdate={updateVariant} onPublish={publishVariant} onRemove={removeVariant} onRewrite={rewriteVariant} onRescrapePrice={handleRescrapePrice} />))}
          </div>
        ) : layoutMode === 'compact' ? (
          <div style={{ display: 'flex', flexDirection: 'column', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            {variants.map(v => (<VariantCard key={v.id} variant={v} layout="compact" slData={slData[v.id]} onUpdate={updateVariant} onPublish={publishVariant} onRemove={removeVariant} onRewrite={rewriteVariant} onRescrapePrice={handleRescrapePrice} />))}
          </div>
        ) : layoutMode === 'split' ? (
          <SplitPaneLayout variants={variants} slData={slData} updateVariant={updateVariant} publishVariant={publishVariant} removeVariant={removeVariant} rewriteVariant={rewriteVariant} handleRescrapePrice={handleRescrapePrice} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: '1.5rem' }}>
            {variants.map(v => (<VariantCard key={v.id} variant={v} slData={slData[v.id]} onUpdate={updateVariant} onPublish={publishVariant} onRemove={removeVariant} onRewrite={rewriteVariant} onRescrapePrice={handleRescrapePrice} />))}
          </div>
        )
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

/* ── Orders Page (imported from OrdersPage.jsx) ── */

/* ── Monitor Page ── */
function MonitorPage() {
  const isMobile = useIsMobile();
  const [catalog] = useState(() => { try { return JSON.parse(localStorage.getItem('amorlia_catalog') || '[]'); } catch { return []; } });
  const [monitorData, setMonitorData] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [experiments, setExperiments] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [userMetrics, setUserMetrics] = useState({ views: '', clicks: '', orders: '', currentPrice: '' });
  const [monitorInterpretation, setMonitorInterpretation] = useState(null);
  const [monitorExperiments, setMonitorExperiments] = useState(null);

  const runMonitor = () => {
    try {
      const monitors = catalog.map(item => {
        const card = item.generatedCard?.data || {};
        return {
          id: item.id,
          title: item.title,
          monitor: {
            status: card.complianceLocked ? 'blocked' : card.finalDecision?.action === 'publish' ? 'healthy' : 'warning',
            smartScore: card.smartScore,
            riskTemperature: card.riskTemperature,
            profitTemperature: card.profitTemperature,
            issues: [
              ...(card.policyLocks || []).map(l => l.message || l),
              ...(card.nextFixes || []).map(f => f),
            ].filter(Boolean),
          },
        };
      });
      setMonitorData(monitors);
    } catch (e) {
      setMonitorData({ error: e.message });
    }
  };

  const runSmartLayerMonitor = async () => {
    const item = catalog.find(c => c.id === selectedItemId);
    if (!item) return;
    const metrics = {
      views: parseInt(userMetrics.views) || 0,
      clicks: parseInt(userMetrics.clicks) || 0,
      orders: parseInt(userMetrics.orders) || 0,
      currentPrice: parseFloat(userMetrics.currentPrice) || (item.price || 0),
    };
    try {
      const product = item.normalized || item.rawPayload || item;
      const monitorResult = await slBuildMonitor(product, metrics, loadSmartLayerSettings());
      if (monitorResult?.ok) {
        const interpretation = await slInterpretMonitor(monitorResult.data);
        const experiments = await slBuildExperiments(monitorResult.data, loadSmartLayerSettings());
        setMonitorInterpretation(interpretation?.ok ? interpretation.data : interpretation);
        setMonitorExperiments(experiments?.ok ? experiments.data : experiments);
      } else {
        setMonitorInterpretation({ error: monitorResult?.error || 'Monitor failed' });
      }
    } catch (e) {
      setMonitorInterpretation({ error: e.message });
    }
  };

  const handleAudit = async () => {
    try {
      const audit = await runReadinessAudit({ userSettings: loadSmartLayerSettings(), records: catalog });
      setAuditResult(audit);
    } catch (e) {
      setAuditResult({ error: e.message });
    }
  };

  return (
    <div>
      <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}>
        <h2 style={{ fontSize: isMobile ? '22px' : '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Monitor</h2>
        <p style={{ color: '#94a3b8', fontSize: isMobile ? '15px' : '0.875rem' }}>Live listing monitoring, portfolio health, and experiment tracking powered by SmartLayer.</p>
      </div>

      {/* Monitor Actions */}
      <div className="glass-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <button onClick={runMonitor} style={{ ...css.btn('#3B82F6', '#2563EB') }}>Run Listing Monitor</button>
          <button onClick={handleAudit} style={{ ...css.btn('#8B5CF6', '#7C3AED') }}>Production Readiness Audit</button>
        </div>
        <p style={{ color: '#475569', fontSize: '0.75rem' }}>Monitoring uses SmartLayer's deterministic analysis on your catalog data. No fake views, clicks, or orders are generated.</p>
      </div>

      {/* SmartLayer Monitor with user-entered metrics */}
      {catalog.length > 0 && (
        <div className="glass-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>SmartLayer Listing Analyzer</h3>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Select a listing and enter your real metrics to get SmartLayer interpretation and experiment ideas.</p>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '100%' : '200px'}, 1fr))`, gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={css.label}>Listing</label>
              <select value={selectedItemId || ''} onChange={e => setSelectedItemId(e.target.value)} style={{ ...css.input, appearance: 'auto' }}>
                <option value="">Select...</option>
                {catalog.map(c => <option key={c.id} value={c.id}>{(c.title || 'Untitled').substring(0, 50)}</option>)}
              </select>
            </div>
            <div><label style={css.label}>Views</label><input type="number" value={userMetrics.views} onChange={e => setUserMetrics(prev => ({ ...prev, views: e.target.value }))} placeholder="0" style={css.input} /></div>
            <div><label style={css.label}>Clicks</label><input type="number" value={userMetrics.clicks} onChange={e => setUserMetrics(prev => ({ ...prev, clicks: e.target.value }))} placeholder="0" style={css.input} /></div>
            <div><label style={css.label}>Orders</label><input type="number" value={userMetrics.orders} onChange={e => setUserMetrics(prev => ({ ...prev, orders: e.target.value }))} placeholder="0" style={css.input} /></div>
            <div><label style={css.label}>Current Price</label><input type="number" step="0.01" value={userMetrics.currentPrice} onChange={e => setUserMetrics(prev => ({ ...prev, currentPrice: e.target.value }))} placeholder="0.00" style={css.input} /></div>
          </div>
          <button onClick={runSmartLayerMonitor} disabled={!selectedItemId} style={{ ...css.btn('#3B82F6', '#2563EB'), opacity: selectedItemId ? 1 : 0.4 }}>Analyze Listing</button>

          {monitorInterpretation && !monitorInterpretation.error && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.3)', borderRadius: 8 }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#f8fafc', marginBottom: '0.25rem' }}>Interpretation</p>
              <pre style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'pre-wrap', margin: 0 }}>{typeof monitorInterpretation === 'string' ? monitorInterpretation : JSON.stringify(monitorInterpretation, null, 2)}</pre>
            </div>
          )}
          {monitorExperiments && !monitorExperiments.error && (
            <div style={{ marginTop: '0.5rem', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.3)', borderRadius: 8 }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#f8fafc', marginBottom: '0.25rem' }}>Experiment Ideas</p>
              <pre style={{ fontSize: '0.75rem', color: '#fbbf24', whiteSpace: 'pre-wrap', margin: 0 }}>{typeof monitorExperiments === 'string' ? monitorExperiments : JSON.stringify(monitorExperiments, null, 2)}</pre>
            </div>
          )}
          {monitorInterpretation?.error && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: '#7f1d1d', borderRadius: 8, fontSize: '0.75rem', color: '#fca5a5' }}>{monitorInterpretation.error}</div>
          )}
        </div>
      )}

      {/* Monitor Results */}
      {monitorData && !monitorData.error && Array.isArray(monitorData) && (
        <div className="glass-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Listing Monitor Results ({monitorData.length} items)</h3>
          {monitorData.map((item, i) => (
            <div key={i} style={{ padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '0.8rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#e2e8f0' }}>{item.title || 'Untitled'}</span>
                <span style={{ color: item.monitor?.status === 'healthy' ? '#2ED573' : item.monitor?.status === 'warning' ? '#FFA502' : '#FF4757' }}>
                  {item.monitor?.status || 'unknown'}
                </span>
              </div>
              {item.monitor?.issues?.length > 0 && item.monitor.issues.map((issue, j) => (
                <div key={j} style={{ color: '#94a3b8', fontSize: '0.7rem', paddingLeft: '1rem' }}>{issue}</div>
              ))}
            </div>
          ))}
        </div>
      )}
      {monitorData?.error && (
        <div style={{ background: '#7f1d1d', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#fca5a5' }}>
          Monitor error: {monitorData.error}
        </div>
      )}

      {/* Audit Results */}
      {auditResult && (
        <div className="glass-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Readiness Audit</h3>
          <div style={{ background: auditResult.readyForProductionStyleUse ? '#14532d' : '#422006', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
            <strong>Production Ready: {auditResult.readyForProductionStyleUse ? 'YES' : 'NOT YET'}</strong>
            {auditResult.recommendation && <p style={{ color: '#fbbf24', marginTop: '0.5rem', fontSize: '0.75rem' }}>{auditResult.recommendation}</p>}
          </div>
        </div>
      )}

      {/* Portfolio Stats */}
      {catalog.length > 0 && (
        <div className="glass-card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Portfolio Snapshot</h3>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '100%' : '200px'}, 1fr))`, gap: '0.75rem' }}>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '0.75rem' }}>
              <div style={{ color: '#64748b', fontSize: '0.7rem' }}>Total Products</div>
              <div style={{ color: '#e2e8f0', fontSize: '1.25rem', fontWeight: 700 }}>{catalog.length}</div>
            </div>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '0.75rem' }}>
              <div style={{ color: '#64748b', fontSize: '0.7rem' }}>Published</div>
              <div style={{ color: '#2ED573', fontSize: '1.25rem', fontWeight: 700 }}>{catalog.filter(c => c.catalogStatus === 'published').length}</div>
            </div>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '0.75rem' }}>
              <div style={{ color: '#64748b', fontSize: '0.7rem' }}>Drafts</div>
              <div style={{ color: '#94a3b8', fontSize: '1.25rem', fontWeight: 700 }}>{catalog.filter(c => c.catalogStatus === 'draft').length}</div>
            </div>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '0.75rem' }}>
              <div style={{ color: '#64748b', fontSize: '0.7rem' }}>With SmartLayer Data</div>
              <div style={{ color: '#818cf8', fontSize: '1.25rem', fontWeight: 700 }}>{catalog.filter(c => c.generatedCard).length}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Pricing Formula Section ── */
function PricingFormulaSection() {
  const pricing = (() => { try { return JSON.parse(localStorage.getItem('amorlia_pricing') || '{}'); } catch { return {}; } })();
  const [local, setLocal] = useState({ markupMultiplier: pricing.markupMultiplier || '5', ebayFeePct: pricing.ebayFeePct || '13.25', shippingCost: pricing.shippingCost || '4.00', processingPct: pricing.processingPct || '3', minMarginPct: pricing.minMarginPct || '40', pricePsychology: pricing.pricePsychology || '99' });
  const [saved, setSaved] = useState(false);
  const update = (key, val) => setLocal(prev => ({ ...prev, [key]: val }));
  const handleSave = () => { localStorage.setItem('amorlia_pricing', JSON.stringify(local)); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const sampleSource = 1.50;
  const markup = parseFloat(local.markupMultiplier) || 5;
  const ebayPct = parseFloat(local.ebayFeePct) || 13.25;
  const ship = parseFloat(local.shippingCost) || 4.00;
  const procPct = parseFloat(local.processingPct) || 3;
  const minMargin = parseFloat(local.minMarginPct) || 40;
  const psych = local.pricePsychology;
  const rawPrice = sampleSource * markup;
  const ebayFee = rawPrice * (ebayPct / 100);
  const procFee = rawPrice * (procPct / 100);
  const totalCost = sampleSource + ebayFee + 0.35 + procFee + ship;
  const netProfit = rawPrice - totalCost;
  const marginPct = rawPrice > 0 ? (netProfit / rawPrice) * 100 : 0;
  let displayPrice = rawPrice;
  if (psych === '99') displayPrice = Math.ceil(rawPrice) - 0.01;
  else if (psych === '95') displayPrice = Math.ceil(rawPrice) - 0.05;
  else displayPrice = Math.round(rawPrice);
  const psychLabel = psych === '99' ? '.99 ending' : psych === '95' ? '.95 ending' : 'Round';
  const inputStyle = { ...css.input, maxWidth: '140px', textAlign: 'center' };
  return (
    <div className="glass-card" style={{ padding: '1.25rem', marginTop: '1.5rem' }}>
      <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Pricing Formula</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem' }}>Configure how selling prices are auto-calculated from source prices.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <div><label style={css.label}>Default Markup Multiplier</label><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="number" step="0.1" min="1" value={local.markupMultiplier} onChange={e => update('markupMultiplier', e.target.value)} style={inputStyle} /><span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>x source price</span></div></div>
        <div><label style={css.label}>eBay Final Value Fee %</label><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="number" step="0.01" value={local.ebayFeePct} onChange={e => update('ebayFeePct', e.target.value)} style={inputStyle} /><span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>%</span></div></div>
        <div><label style={css.label}>Estimated Shipping Cost</label><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><span style={{ color: '#94a3b8' }}>$</span><input type="number" step="0.01" value={local.shippingCost} onChange={e => update('shippingCost', e.target.value)} style={inputStyle} /></div></div>
        <div><label style={css.label}>Payment Processing Fee %</label><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="number" step="0.01" value={local.processingPct} onChange={e => update('processingPct', e.target.value)} style={inputStyle} /><span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>%</span></div></div>
        <div><label style={css.label}>Minimum Acceptable Margin %</label><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="number" step="1" value={local.minMarginPct} onChange={e => update('minMarginPct', e.target.value)} style={inputStyle} /><span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>%</span></div></div>
        <div><label style={css.label}>Price Psychology</label><div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>{[{ value: '99', label: '.99 endings' }, { value: '95', label: '.95 endings' }, { value: 'round', label: 'Round numbers' }].map(opt => (<button key={opt.value} onClick={() => update('pricePsychology', opt.value)} style={{ flex: 1, padding: '0.5rem 0.25rem', background: local.pricePsychology === opt.value ? '#1e3a5f' : '#0f172a', border: `1px solid ${local.pricePsychology === opt.value ? '#3b82f6' : '#334155'}`, borderRadius: '0.375rem', color: local.pricePsychology === opt.value ? '#93c5fd' : '#64748b', fontSize: '0.7rem', cursor: 'pointer', fontWeight: local.pricePsychology === opt.value ? 700 : 400 }}>{opt.label}</button>))}</div></div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #1e293b', marginBottom: '1rem' }}>
        <p style={{ ...css.label, marginBottom: '0.5rem' }}>Live Preview (sample source: $1.50)</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', fontSize: '0.8rem' }}>
          <div><span style={{ color: '#64748b' }}>Raw ({markup}x)</span><div style={{ color: '#f8fafc', fontWeight: 700, fontFamily: 'monospace' }}>${rawPrice.toFixed(2)}</div></div>
          <div><span style={{ color: '#64748b' }}>After {psychLabel}</span><div style={{ color: '#22c55e', fontWeight: 700, fontFamily: 'monospace' }}>${displayPrice.toFixed(2)}</div></div>
          <div><span style={{ color: '#64748b' }}>Est. Margin</span><div style={{ color: marginPct >= minMargin ? '#22c55e' : marginPct >= 25 ? '#f59e0b' : '#ef4444', fontWeight: 700, fontFamily: 'monospace' }}>{marginPct.toFixed(1)}%</div></div>
        </div>
      </div>
      <button onClick={handleSave} style={css.btn('#2ED573', '#25B563')}>{Icon.check(16)} Save Pricing Formula</button>
      {saved && <span style={{ color: '#2ED573', fontSize: '0.85rem', marginLeft: '0.75rem' }}>Pricing formula saved.</span>}
    </div>
  );
}

/* ── Settings Page ── */
function SettingsPage() {
  const [settings, setSettings] = useState(() => { try { return JSON.parse(localStorage.getItem('amorlia_settings') || '{}'); } catch { return {}; } });
  const [saved, setSaved] = useState(false);
  const [slSettings, setSlSettings] = useState(() => loadSmartLayerSettings());
  const [slSaved, setSlSaved] = useState(false);
  const [slValidation, setSlValidation] = useState(null);
  const [selfTestResult, setSelfTestResult] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const isMobile = useIsMobile();

  const fields = [
    { key: 'profitFloor', label: 'Profit Floor ($)', type: 'number', default: '8.00', desc: 'Minimum profit per item after fees' },
    { key: 'scrapeTimeout', label: 'Scrape Timeout (seconds)', type: 'number', default: '30', desc: 'Max time for Puppeteer scrape before timeout.' },
    { key: 'ebayDevId', label: 'eBay Dev ID', type: 'text', default: '' },
    { key: 'ebayAppId', label: 'eBay App ID', type: 'text', default: '' },
    { key: 'ebayCertId', label: 'eBay Cert ID', type: 'text', default: '' },
    { key: 'ebayUserToken', label: 'eBay User Token', type: 'text', default: '', desc: 'OAuth user token from eBay developer portal' },
    { key: 'dashboardPassword', label: 'Dashboard Password', type: 'text', default: 'amorlia_secure_123' },
    { key: 'paypalEmail', label: 'PayPal Email', type: 'text', default: '' },
    { key: 'monthlyFixedCosts', label: 'Monthly Fixed Costs ($)', type: 'number', default: '0', desc: 'eBay subscription, tools, etc. Used for break-even tracking.' },
    { key: 'defaultShippingDays', label: 'Default Shipping Days', type: 'number', default: '14', desc: 'Estimated shipping time from supplier in days' },
  ];

  const slFields = [
    { key: 'platform', label: 'Platform', type: 'select', options: ['ebay', 'etsy', 'shopify', 'generic'], desc: 'Target marketplace for listing optimization' },
    { key: 'minProfitDollars', label: 'Min Profit ($)', type: 'number', default: 5, desc: 'Minimum dollar profit per item' },
    { key: 'targetMargin', label: 'Target Margin', type: 'number', default: 0.42, step: 0.01, desc: 'Target profit margin (0.42 = 42%)' },
    { key: 'platformFeeEstimate', label: 'Platform Fee %', type: 'number', default: 0.14, step: 0.01, desc: 'Estimated platform fee rate' },
    { key: 'paymentFeeEstimate', label: 'Payment Fee %', type: 'number', default: 0.035, step: 0.001, desc: 'Payment processing fee rate' },
    { key: 'adBufferRate', label: 'Ad Buffer %', type: 'number', default: 0.12, step: 0.01, desc: 'Advertising cost buffer' },
    { key: 'rulesetProfile', label: 'Ruleset Profile', type: 'select', options: ['conservative', 'balanced', 'testing', 'lowTicketVolume', 'boutique'], desc: 'Smart Layer compliance profile' },
  ];

  const slToggles = [
    { key: 'strictCompliance', label: 'Strict Compliance', desc: 'Extra compliance checks on listing copy' },
    { key: 'avoidDropshippingLanguage', label: 'Avoid Dropship Language', desc: 'Remove supplier/dropshipping terms from listing' },
    { key: 'avoidFakeLuxuryClaims', label: 'Avoid Fake Luxury Claims', desc: 'Block counterfeit or dupe language' },
    { key: 'avoidMedicalClaims', label: 'Avoid Medical Claims', desc: 'Block health/medical outcome claims' },
    { key: 'avoidAbsoluteClaims', label: 'Avoid Absolute Claims', desc: 'Block "never tarnish" "100% waterproof" etc.' },
  ];

  const handleChange = (key, value) => { setSettings(prev => ({ ...prev, [key]: value })); setSaved(false); };
  const handleSave = () => { localStorage.setItem('amorlia_settings', JSON.stringify(settings)); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const handleSlChange = (key, value) => { setSlSettings(prev => ({ ...prev, [key]: value })); setSlSaved(false); setSlValidation(null); };
  const handleSlToggle = (key) => { setSlSettings(prev => ({ ...prev, [key]: !prev[key] })); setSlSaved(false); setSlValidation(null); };
  const handleSlSave = async () => {
    saveSmartLayerBridgeSettings(slSettings);
    setSlSaved(true);
    setSlValidation(await validateSmartLayerBridgeSettings(slSettings));
    setTimeout(() => setSlSaved(false), 2000);
  };

  const handleSelfTest = async () => {
    try {
      const result = await runSelfTest(slSettings);
      setSelfTestResult(result);
    } catch (e) {
      setSelfTestResult({ passed: false, error: e.message });
    }
  };

  const handleAudit = async () => {
    try {
      const result = await runReadinessAudit({ userSettings: slSettings, records: [], payloads: [] });
      setAuditResult(result);
    } catch (e) {
      setAuditResult({ readyForProductionStyleUse: false, error: e.message });
    }
  };

  return (
    <div>
      <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}>
        <h2 style={{ fontSize: isMobile ? '22px' : '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Settings</h2>
        <p style={{ color: '#94a3b8', fontSize: isMobile ? '15px' : '0.875rem' }}>Configure API keys, pricing, and preferences.</p>
      </div>
      <div className="glass-card" style={{ padding: isMobile ? '16px' : '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '100%' : '380px'}, 1fr))`, gap: '1rem', marginBottom: '1.5rem' }}>
          {fields.map(f => (<div key={f.key}><label style={css.label}>{f.label}</label><input type={f.type} value={settings[f.key] ?? f.default} onChange={e => handleChange(f.key, e.target.value)} style={css.input} />{f.desc && <p style={{ fontSize: '0.7rem', color: '#475569', marginTop: '0.25rem' }}>{f.desc}</p>}</div>))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={handleSave} style={css.btn('#2ED573', '#25B563')}>{Icon.check(16)} Save Settings</button>
          {saved && <span style={{ color: '#2ED573', fontSize: '0.85rem' }}>Settings saved locally.</span>}
        </div>
      </div>

      {/* SmartLayer Settings */}
      <div className="glass-card" style={{ padding: isMobile ? '16px' : '1.25rem', marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Smart Layer Settings</h3>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem' }}>Configure how Smart Layer processes, scores, and generates listing intelligence.</p>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '100%' : '280px'}, 1fr))`, gap: '1rem', marginBottom: '1rem' }}>
          {slFields.map(f => (
            <div key={f.key}>
              <label style={css.label}>{f.label}</label>
              {f.type === 'select' ? (
                <select value={slSettings[f.key] || f.default} onChange={e => handleSlChange(f.key, e.target.value)} style={{ ...css.input, appearance: 'auto' }}>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type} step={f.step || '1'} value={slSettings[f.key] ?? f.default} onChange={e => handleSlChange(f.key, e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value)} style={css.input} />
              )}
              {f.desc && <p style={{ fontSize: '0.7rem', color: '#475569', marginTop: '0.25rem' }}>{f.desc}</p>}
            </div>
          ))}
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ ...css.label, marginBottom: '0.5rem' }}>Compliance Toggles</p>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '100%' : '250px'}, 1fr))`, gap: '0.5rem' }}>
            {slToggles.map(t => (
              <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 0.75rem', background: slSettings[t.key] ? 'rgba(46,213,115,0.08)' : 'rgba(255,255,255,0.02)', borderRadius: 8, border: `1px solid ${slSettings[t.key] ? 'rgba(46,213,115,0.2)' : '#1e293b'}` }}>
                <input type="checkbox" checked={!!slSettings[t.key]} onChange={() => handleSlToggle(t.key)} style={{ accentColor: '#2ED573' }} />
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#e2e8f0', fontWeight: 600 }}>{t.label}</span>
                  <p style={{ fontSize: '0.65rem', color: '#64748b', margin: 0 }}>{t.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button onClick={handleSlSave} style={css.btn('#2ED573', '#25B563')}>{Icon.check(16)} Save Smart Layer Settings</button>
          {slSaved && <span style={{ color: '#2ED573', fontSize: '0.85rem' }}>Smart Layer settings saved.</span>}
          {slValidation && (
            <span style={{ fontSize: '0.8rem', color: slValidation.valid ? '#2ED573' : '#FF4757' }}>
              {slValidation.valid ? 'Valid' : `Issues: ${slValidation.issues?.length || 0}`}
            </span>
          )}
        </div>
      </div>

      {/* SmartLayer Diagnostics */}
      <div className="glass-card" style={{ padding: isMobile ? '16px' : '1.25rem', marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Smart Layer Diagnostics</h3>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button onClick={handleSelfTest} style={{ ...css.btn('#3B82F6', '#2563EB'), fontSize: '0.8rem' }}>Run Self Test</button>
          <button onClick={handleAudit} style={{ ...css.btn('#8B5CF6', '#7C3AED'), fontSize: '0.8rem' }}>Production Readiness Audit</button>
        </div>
        {selfTestResult && (
          <div style={{ background: selfTestResult.passed ? '#14532d' : '#7f1d1d', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
            <strong>Self Test: {selfTestResult.passed ? 'PASSED' : 'FAILED'}</strong>
            <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>{selfTestResult.totalChecks || '?'} checks</span>
            {selfTestResult.failed?.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {selfTestResult.failed.map((f, i) => <div key={i} style={{ color: '#fca5a5', fontSize: '0.75rem' }}>FAIL: {f.id} — {f.label}</div>)}
              </div>
            )}
          </div>
        )}
        {auditResult && (
          <div style={{ background: auditResult.readyForProductionStyleUse ? '#14532d' : '#422006', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
            <strong>Production Ready: {auditResult.readyForProductionStyleUse ? 'YES' : 'NOT YET'}</strong>
            {auditResult.checks?.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {auditResult.checks.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    <span style={{ color: c.passed ? '#2ED573' : '#FF4757' }}>{c.passed ? 'PASS' : 'FAIL'}</span>
                    <span style={{ color: '#94a3b8' }}>{c.label}</span>
                  </div>
                ))}
              </div>
            )}
            {auditResult.recommendation && <p style={{ color: '#fbbf24', marginTop: '0.5rem', fontSize: '0.75rem' }}>{auditResult.recommendation}</p>}
          </div>
        )}
      </div>

      <PricingFormulaSection />
      <div className="glass-card" style={{ padding: '1.25rem', marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Render Environment Variables</h3>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Set these in Render dashboard under your service Environment tab:</p>
        <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '0.5rem', padding: '1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#00D4AA' }}>
          <div>NIHAO_EMAIL=your@email.com</div>
          <div>NIHAO_PASSWORD=yourpassword</div>
          <div>PROFIT_FLOOR=8.00</div>
          <div>EBAY_DEV_ID=...</div>
          <div>EBAY_APP_ID=...</div>
          <div>EBAY_CERT_ID=...</div>
          <div>EBAY_USER_TOKEN=...</div>
        </div>
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,107,53,0.1)', borderRadius: '0.5rem', border: '1px solid rgba(255,107,53,0.2)' }}>
          <p style={{ color: '#FF6B35', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Bookmarklet</p>
          <p style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Use the Amorlia Select bookmarklet on any Nihao category page to grab multiple products at once. They will load here automatically when you open the dashboard.</p>
        </div>
      </div>
    </div>
  );
}

/* ── Main App ── */
const DASHBOARD_PASSWORD = 'amorlia_secure_123';

export default function App() {
  const { user, signOut, isConfigured } = useAuth();
  const [authed, setAuthed] = useState(() => {
    try {
      const stored = localStorage.getItem('amorlia_auth');
      if (!stored) return false;
      const decoded = atob(stored);
      return decoded === DASHBOARD_PASSWORD || decoded === (process.env.REACT_APP_DASHBOARD_PASSWORD || '');
    } catch { return false; }
  });
  const [page, setPage] = useState(() => {
    try { return JSON.parse(localStorage.getItem('amorlia_last_active_page') || '"home"'); } catch { return 'home'; }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isMobile = useIsMobile();

  // Persist last active page
  useEffect(() => {
    try { localStorage.setItem('amorlia_last_active_page', JSON.stringify(page)); } catch {}
  }, [page]);

  const handleLegacyLogin = useCallback(() => {
    localStorage.setItem('amorlia_auth', btoa(DASHBOARD_PASSWORD));
    setAuthed(true);
  }, []);

  const handleLogout = useCallback(() => {
    if (user) signOut();
    localStorage.removeItem('amorlia_auth');
    setAuthed(false);
  }, [user, signOut]);

  // Show login if neither Supabase auth nor legacy auth
  if (!user && !authed) return <LoginScreen onLegacyLogin={handleLegacyLogin} />;

  const pages = {
    home: () => <HomePage setPage={setPage} />,
    extract: ExtractPage,
    catalog: CatalogPage,
    orders: OrdersPage,
    monitor: MonitorPage,
    settings: SettingsPage,
    analytics: AnalyticsPage,
    research: () => <PlaceholderPage title="Research & Trends" description="Trending products, ad spy, and competitor analysis. Coming in Phase 2." icon={Icon.research} />,
    'ai-tools': () => <PlaceholderPage title="AI Tools" description="Batch AI optimization, smart pricing, and automated listing enhancement. Coming in Phase 2." icon={Icon.sparkle} />,
  };
  const PageComponent = pages[page] || pages.home;
  const sidebarWidth = sidebarCollapsed ? 60 : 220;

  if (isMobile) {
    return (
      <AmorliaDashboardProvider>
        <MobileLayout page={page} setPage={setPage}>
          <div className={`page-${page}`} style={{ minHeight: '100vh', padding: '20px 16px 0' }}>
            <Suspense fallback={<div style={{ color: 'var(--color-text-secondary)', padding: '2rem' }}>Loading...</div>}>
              <PageComponent />
            </Suspense>
          </div>
        </MobileLayout>
      </AmorliaDashboardProvider>
    );
  }

  return (
    <AmorliaDashboardProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--color-bg-base)' }}>
        <Sidebar
          page={page}
          setPage={setPage}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className={`page-${page}`} style={{ flex: 1, marginLeft: sidebarWidth, padding: '2rem', maxWidth: '1200px', transition: 'margin-left 0.2s ease' }}>
          <Suspense fallback={<div style={{ color: 'var(--color-text-secondary)', padding: '2rem' }}>Loading...</div>}>
            <PageComponent />
          </Suspense>
        </main>
      </div>
    </AmorliaDashboardProvider>
  );
}

/* ── Placeholder page for stubs ── */
function PlaceholderPage({ title, description, icon }) {
  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {icon && icon(20)} {title}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>{description}</p>
      </div>
      <div className="glass-card" style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>{icon && icon(48)}</div>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>Coming Soon</h3>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', maxWidth: 400, margin: '0 auto' }}>This feature is planned for a future update. Your feedback helps us prioritize what to build next.</p>
      </div>
    </div>
  );
}